import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { createRoot } from "react-dom/client";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  getProject,
  getBlocks,
  // getParcels, // Removed - using background loading
  getProjectParcelsGeoJsonStatic,
  getProjectMapLayers,
  searchParcels,
  getProjectLandmarks,
  getParcelWithProject,
  toggleFavorite,
  getFloorUnitsGeoJson,
  getFloorBasemap,
} from "../../services/api";
import spacesService from "../../services/spacesService";
import { useProjects } from "../../contexts/ProjectsContext";
// LoadingOverlay removed - using background loading instead
import LayersLoadingIndicator from "./LayersLoadingIndicator";
import MapControls from "./MapControls";
import ParcelPopupContent from "./ParcelPopupContent";
import ParcelDetailsModal from "./ParcelDetailsModal";
import RealMapLogo from "./RealMapLogo";
import RightControls from "./RightControls";
import MapFilters from "../Filters/MapFilters";
import TermsNotification from "../Terms/TermsNotification";
import ProjectDetailsPanel from "./ProjectDetailsPanel";
import { getTranslation } from "../../utils/translations";
import {
  MAPBOX_CONFIG,
  APP_PATHS,
  APP_SETTINGS,
  SPACES_CONFIG,
  getMapLayerFileUrl,
  getStorageUrl,
} from "../../config/constants";
import { useMapConfig } from "../../hooks/useSettings";
import { useProjectDisplay } from "../../hooks/useProjectDisplay";
import MapProjectService from "../../services/MapProjectService";
import MapLayerManager from "../../services/MapLayerManager";
import MapLogger from "../../utils/MapLogger";
import MapErrorHandler from "../../utils/MapErrorHandler";


mapboxgl.accessToken = MAPBOX_CONFIG.ACCESS_TOKEN;

const OptimizedMap = ({
  selectedProject,
  onProjectChange,
  mapStyle = "streets-v11",
  onMapStyleChange,
  onLanguageToggle,
  onSidebarPositionToggle,
  language = "ar",
  sidebarPosition = "auto",
  actualSidebarPosition = "right",
  onParcelSearch,
  onMapReady,
  filtersVisible = false,
  onToggleFilters,
  filters = null,
  statusFilters = [],
  priceFilter = { min: 0, max: 1000000 },
  usageFilters = [],
  floorFilters = [],
  projectFloors = [],
  projectFilters = { status: [], city: "" },
  isSharedView = false,
  sharedProjectId = null,
  isSharedUnitView = false,
  sharedUnitId = null,
  onZoomChange,
  onReserve,
  onShowFavorites,
  onPriceStatsUpdate,
  onClearAllData,
  onMapInteraction,
  onParcelSelect,
  onProjectSelect,
  pendingUnitSelect = null,
  mapRef: externalMapRef,
}) => {
  // Load dynamic map configuration
  const {
    mapConfig,
    loading: configLoading,
    forceRefreshMapConfig,
  } = useMapConfig();

  // Define map refs first
  const mapContainerRef = useRef(null);
  const mapRef = externalMapRef || useRef(null);

  // Parcel details modal state (needed for click handler)
  const [selectedParcelData, setSelectedParcelData] = useState(null);
  const [selectedParcelModalData, setSelectedParcelModalData] = useState(null);
  const [isParcelModalVisible, setIsParcelModalVisible] = useState(false);
  const [parcelModalLoading, setParcelModalLoading] = useState(false);
  const [parcelModalError, setParcelModalError] = useState(false);

  // Track if project was clicked directly to prevent duplicate calls
  const projectClickedDirectlyRef = useRef(false);

  // Ref for selectedProjectData to prevent dependency cycles
  const selectedProjectDataRef = useRef(null);

  // Track pending unit selection so the external-project-selection effect
  // can skip opening the project card while we're navigating to a unit.
  const pendingUnitSelectRef = useRef(null);
  useEffect(() => {
    pendingUnitSelectRef.current = pendingUnitSelect;
  }, [pendingUnitSelect]);

  // Parcel click handler for the map service
  const handleParcelClick = useCallback((parcelData) => {
    // Always highlight the selected parcel on the map
    setSelectedParcelId(parcelData.id);

    // Refresh dimensions overlay to track the newly-selected parcel.
    // Wait until the camera settles so the dimension lines/labels are
    // drawn against the final geometry — drawing mid-flyTo produces
    // misaligned points and wrong-looking distances on screen.
    hideParcelDimensions();
    if (parcelData && parcelData.coordinates) {
      const drawDimensions = () => {
        if (mapRef.current) showParcelDimensions(parcelData);
      };
      if (mapRef.current && (mapRef.current.isMoving() || mapRef.current.isZooming() || mapRef.current.isEasing())) {
        mapRef.current.once("moveend", drawDimensions);
      } else {
        drawDimensions();
      }
    }

    if (onParcelSelect) {
      onParcelSelect(parcelData, selectedProjectDataRef.current);
    } else {
      setSelectedParcelData(parcelData);
      setIsParcelModalVisible(true);
      setSelectedParcelModalData(parcelData);
      setParcelModalLoading(true);
      setParcelModalError(false);
    }
  }, [onParcelSelect]);

  // Use project display hook
  const {
    displayResidentialProject,
    displayLandProject,
    applyFilters: applyProjectFilters,
    clearCurrentProject,
    reloadCurrentProject,
    getCurrentProjectInfo,
    hasCurrentProject,
    isCurrentProjectType,
    clearError,
    reset: resetProjectDisplay,
    currentProject,
    currentProjectType,
    isLoading: isProjectLoading,
    lastError: projectError,
    currentFloorFilters
  } = useProjectDisplay(mapRef, handleParcelClick);

  // Ref to store map interaction handler
  const mapInteractionHandlerRef = useRef(null);
  const userInteractionHandlerRef = useRef(null);
  
  // Track if initial animation has completed
  const initialAnimationCompleteRef = useRef(false);
  const userInteractionStartedRef = useRef(false);

  // Track if we are currently searching for a unit to prevent project panels from opening
  const isSearchingUnitRef = useRef(false);

  // Add periodic refresh of settings (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      if (forceRefreshMapConfig) {
        forceRefreshMapConfig();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [forceRefreshMapConfig]);

  const popupRef = useRef(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [selectedParcelId, setSelectedParcelId] = useState(null);
  // Loading states removed - using background loading instead

  // Layers loading state - kept for compatibility with existing functions
  const [isLayersLoading, setIsLayersLoading] = useState(false);
  const [layersLoadingMessage, setLayersLoadingMessage] = useState("");

  // Use projects context
  const { projects, loading: projectsLoading, loadProjects ,selectedFloor ,setSelectedFloor} = useProjects();
  const projectMarkersRef = useRef([]);
  const [mapLayers, setMapLayers] = useState([]);
  const [isParcelPopoverVisible, setIsParcelPopoverVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [allParcelsData, setAllParcelsData] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    salesStatus: [],
    priceRange: { min: 0, max: 1000000 },
    services: [],
  });
  const [parcelFavoriteStatus, setParcelFavoriteStatus] = useState({});
  const [favoriteLoadingStatus, setFavoriteLoadingStatus] = useState({});
  const [isPriceFilterExplicitlyActive, setIsPriceFilterExplicitlyActive] =
    useState(false);
  const [currentZoom, setCurrentZoom] = useState(MAPBOX_CONFIG.DEFAULT_ZOOM);
  const [userSelectedStyle, setUserSelectedStyle] = useState(null); // Track if user manually selected a style

  // Project details panel state
  const [selectedProjectData, setSelectedProjectData] = useState(null);
  
  // Sync selectedProjectData with ref
  useEffect(() => {
    selectedProjectDataRef.current = selectedProjectData;
  }, [selectedProjectData]);

  const [isProjectPanelVisible, setIsProjectPanelVisible] = useState(false);

  // Unit details visibility state
  const [isUnitDetailsVisible, setIsUnitDetailsVisible] = useState(false);

  // Shared project processing state
  const [sharedProjectProcessed, setSharedProjectProcessed] = useState(false);

  useEffect(() => {
    
    if (isProjectPanelVisible) {
      if(selectedProject){
        const project = projects.find((p) => p.id === selectedProject);
        if(project && project.project_type === "residential"){

          // get floors from projectFloors prop (loaded via getProjectFloors API)
          const floors = projectFloors && projectFloors.length > 0
            ? projectFloors
            : (selectedProjectData?.floors || []);

          if (floors.length === 0) return;

          // get floor floor_number 1 or first available floor
          const floor =selectedFloor != null ? selectedFloor :floors.find((f) => f.floor_number === 1) || floors[0];
          if(!selectedFloor){
            setSelectedFloor(floor);
          }
          loadFloorData([floor.id],selectedProject);
        }
      }
      // loadFloorData(floorFilters,selectedProject);
    }
  }, [isProjectPanelVisible, projectFloors]);
  // Project panel handlers
  const handleCloseProjectPanel = () => {
    setIsProjectPanelVisible(false);
    setSelectedProjectData(null);
  };

  // Parcel modal handlers
  const handleCloseParcelModal = () => {
    setIsParcelModalVisible(false);
    setSelectedParcelModalData(null);
    setParcelModalLoading(false);
    setParcelModalError(false);
    // Hide parcel dimensions when modal closes
    hideParcelDimensions();
    
    // Show project panel again when unit details are closed
    if (selectedProjectData) {
      setIsProjectPanelVisible(true);
    }
  };

  const handleHomeClick = () => {
    // 1. Hide unit details popup if visible
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    // 2. Hide parcel details modal if visible
    if (isParcelModalVisible) {
      handleCloseParcelModal();
    }

    // 3. Clear all selected project and floor data
    setSelectedProjectData(null);
    setIsProjectPanelVisible(false);
    setIsUnitDetailsVisible(false);

    // 4. Remove floor basemap overlay when going home
    removeFloorBasemapOverlay();

    // 5. Remove parcels source and layers when going home
    if (mapRef.current && mapRef.current.getSource("parcels")) {
      // Remove layers first
      const layersToRemove = [
        "parcels-fill",
        "parcels-outline",
        "parcels-labels",
        "parcels-prices",
      ];
      layersToRemove.forEach((layerId) => {
        if (mapRef.current.getLayer(layerId)) {
          mapRef.current.removeLayer(layerId);
        }
      });
      mapRef.current.removeSource("parcels");
    }

    // 6. Remove all floor layers and sources
    if (mapRef.current) {
      // Remove all floor-related layers
      const floorLayersToRemove = [
        "floor-fill",
        "floor-outline", 
        "floor-labels",
        "floor-prices"
      ];
      
      floorLayersToRemove.forEach((layerId) => {
        if (mapRef.current.getLayer(layerId)) {
          mapRef.current.removeLayer(layerId);
        }
      });

      // Remove all floor sources
      const sources = mapRef.current.getStyle().sources;
      Object.keys(sources).forEach(sourceId => {
        if (sourceId.startsWith('floor-') && mapRef.current.getSource(sourceId)) {
          mapRef.current.removeSource(sourceId);
        }
      });
    }

    // 7. Call parent component to clear all data
    if (onClearAllData) {
      onClearAllData();
    }

    // Clear current project using the project display hook
    clearCurrentProject();

    // if (mapRef.current) {
    //   mapRef.current.flyTo({
    //     center: [45.0792, 23.8859], // Saudi Arabia center
    //     zoom: 6,
    //   });
    // }
  };
  // Sub home button handler
  const handleSubHomeClick = async () => {
    try {
      // Mark that sub-home was clicked to prevent duplicate calls
      projectClickedDirectlyRef.current = true;

      // 1. Hide unit details popup if visible
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }

      // 2. Hide parcel details modal if visible
      if (isParcelModalVisible) {
        handleCloseParcelModal();
      }

      // Clear unit details state
      setSelectedParcelId(null);
      setSelectedParcelData(null);
      setIsParcelPopoverVisible(false);
      setIsUnitDetailsVisible(false);

      // Hide parcel dimensions
      hideParcelDimensions();

      // Clear active parcel reference
      activeParcelRef.current = null;

      // 3. For residential projects, reset floor to floor_number = 1 or first available floor
      if (selectedProject && projects.length > 0) {
        const currentProject = projects.find((p) => p.id === selectedProject);

        if (currentProject && currentProject.project_type === "residential") {
          // Get floors from projectFloors prop (loaded via getProjectFloors API)
          const floors = projectFloors && projectFloors.length > 0
            ? projectFloors
            : (currentProject.floors || []);

          if (floors.length === 0) return;

          // Find floor with floor_number = 1 or first available floor
          const floor = selectedFloor != null ? selectedFloor : floors.find((f) => f.floor_number === 1) || floors[0];
          if(!selectedFloor){
            setSelectedFloor(floor);
          }

          if (floor) {
            // Load floor data immediately
            await loadFloorData([floor.id], selectedProject);
          }
        }
      }

      // 4. Zoom to project first, then show project details panel
      if (selectedProject && projects.length > 0) {
        const currentProject = projects.find((p) => p.id === selectedProject);

        if (
          currentProject &&
          currentProject.latitude &&
          currentProject.longitude
        ) {
          const coordinates = [
            parseFloat(currentProject.longitude),
            parseFloat(currentProject.latitude),
          ];

          // Use zoom level 18 for residential projects, 14 for others
          const zoomLevel =
            currentProject.map_zoom_level !== null && currentProject.map_zoom_level !== undefined 
              ? currentProject.map_zoom_level 
              : (currentProject.project_type === "residential" ? 18 : 14);
          const flyToOptions = {
            center: coordinates,
            zoom: zoomLevel,
            essential: false,
          };

         

          // Start flying to location
          mapRef.current.flyTo(flyToOptions);

          // Wait for the fly animation to complete before showing project details
          mapRef.current.once("moveend", async () => {
            try {

              // Load landmarks for the project
              drowLandMark(currentProject.id).catch((error) => {
                console.error("❌ Error loading landmarks for project:", error);
              });

              // Add parcels source and layers for the project
              addParcelsSourceAndLayers(currentProject.id);

              // Only show project details if we aren't zooming into a specific unit
              if (!isSearchingUnitRef.current) {
                if (
                  currentProject.statistics &&
                  Object.keys(currentProject.statistics).length > 0
                ) {
                  if (onProjectSelect) {
                    onProjectSelect(currentProject);
                  } else {
                    setSelectedProjectData(currentProject);
                    setIsProjectPanelVisible(true);
                  }
                } else {
                  const response = await getProject(currentProject.id);
                  const projectData = response.data.data;
                  if (onProjectSelect) {
                    onProjectSelect(projectData);
                  } else {
                    setSelectedProjectData(projectData);
                    setIsProjectPanelVisible(true);
                  }
                }
              }

            } catch (error) {
              console.error(
                "Error fetching project details after zoom:",
                error
              );
              if (!isSearchingUnitRef.current) {
                if (onProjectSelect) {
                  onProjectSelect(currentProject);
                } else {
                  setSelectedProjectData(currentProject);
                  setIsProjectPanelVisible(true);
                }
              }
            }
          });
        } else {
          // Fallback: show project details without zoom
          try {
            const response = await getProject(currentProject.id);
            const projectData = response.data.data;
            if (!isSearchingUnitRef.current) {
              if (onProjectSelect) {
                onProjectSelect(projectData);
              } else {
                setSelectedProjectData(projectData);
                setIsProjectPanelVisible(true);
              }
            }
          } catch (error) {
            console.error("Error fetching project details:", error);
            if (!isSearchingUnitRef.current) {
              if (onProjectSelect) {
                onProjectSelect(currentProject);
              } else {
                setSelectedProjectData(currentProject);
                setIsProjectPanelVisible(true);
              }
            }
          }
        }
      } else {
        
      }
    } catch (error) {
      console.error("Error in handleSubHomeClick:", error);
    }
  };

  // Helper function to safely create bounds from coordinates
  const createSafeBounds = (coordinates) => {
    const bounds = new mapboxgl.LngLatBounds();
    let validCoordinatesCount = 0;

    if (coordinates && coordinates[0]) {
      coordinates[0].forEach((coord) => {
        // Ensure coord is a valid array with two numbers
        if (
          Array.isArray(coord) &&
          coord.length >= 2 &&
          typeof coord[0] === "number" &&
          typeof coord[1] === "number" &&
          !isNaN(coord[0]) &&
          !isNaN(coord[1])
        ) {
          bounds.extend(coord);
          validCoordinatesCount++;
        } else {
          
        }
      });
    }

    return { bounds, isValid: validCoordinatesCount > 0 && !bounds.isEmpty() };
  };
  // Dynamic map style switching based on zoom level
  const handleDynamicStyleSwitch = useCallback(
    (zoom) => {
    
      // Only apply dynamic switching for satellite styles and if user hasn't manually selected a style
      if (
        !userSelectedStyle &&
        (mapStyle === "satellite-v9" || mapStyle === "satellite-streets-v12")
      ) {
        const shouldShowStreetDetails =
          zoom >= APP_SETTINGS.MAP.STREET_DETAILS_ZOOM;
        const currentIsSatelliteStreets = mapStyle === "satellite-streets-v12";

        if (shouldShowStreetDetails && !currentIsSatelliteStreets) {
          // Switch to satellite with street details
          //   `🗺️ Zoom ${zoom}: Switching to satellite-streets-v12 (detailed view)`
          // );
          // onMapStyleChange('satellite-streets-v12');
        } else if (!shouldShowStreetDetails && currentIsSatelliteStreets) {
          // Switch to simple satellite
          //   `🗺️ Zoom ${zoom}: Switching to satellite-v9 (simple view)`
          // );
          // onMapStyleChange('satellite-v9');
        }
      }
    },
    [mapStyle, userSelectedStyle, onMapStyleChange]
  );

  // Function to calculate distance between two geographic points in meters
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Function to calculate parcel dimensions from polygon coordinates
  const calculateParcelDimensions = (coordinates) => {

    // Handle different coordinate formats
    let ring = null;

    if (!coordinates) {
      console.error("No coordinates provided");
      return { dimensions: [], center: null };
    }

    // Case 1: Single coordinate pair [lng, lat] - not a polygon
    if (
      Array.isArray(coordinates) &&
      coordinates.length === 2 &&
      typeof coordinates[0] === "number" &&
      typeof coordinates[1] === "number"
    ) {
        
      return { dimensions: [], center: null };
    }

    // Case 2: coordinates is already a ring (array of coordinate pairs)
    if (
      Array.isArray(coordinates) &&
      coordinates.length > 0 &&
      Array.isArray(coordinates[0])
    ) {
      // Check if coordinates[0] is a coordinate pair [lng, lat] or another array
      if (
        coordinates[0].length === 2 &&
        typeof coordinates[0][0] === "number"
      ) {
        ring = coordinates;
      } else if (Array.isArray(coordinates[0]) && coordinates[0].length > 0) {
        // coordinates[0] is actually the ring
        ring = coordinates[0];
      }
    }
    // Case 3: coordinates[0] is the ring (GeoJSON Polygon format)
    else if (
      Array.isArray(coordinates) &&
      Array.isArray(coordinates[0]) &&
      coordinates[0].length > 0 &&
      Array.isArray(coordinates[0][0])
    ) {
      ring = coordinates[0];
    }
    // Case 4: coordinates.coordinates exists (nested GeoJSON)
    else if (
      coordinates.coordinates &&
      Array.isArray(coordinates.coordinates)
    ) {
      if (
        Array.isArray(coordinates.coordinates[0]) &&
        Array.isArray(coordinates.coordinates[0][0])
      ) {
        ring = coordinates.coordinates[0];
      } else if (Array.isArray(coordinates.coordinates[0])) {
        ring = coordinates.coordinates;
      }
    }

    // Case 5: coordinates[0] is a number (invalid polygon format)
    else if (Array.isArray(coordinates) && typeof coordinates[0] === "number") {
      console.error(
        "Invalid polygon format - coordinates[0] is a number:",
        coordinates[0]
      );
      return { dimensions: [], center: null };
    }

    // Validate the ring
    if (!ring || !Array.isArray(ring)) {
      console.error(
        "Could not extract valid ring from coordinates:",
        coordinates
      );
      return { dimensions: [], center: null };
    }

    if (ring.length < 3) {
      console.error("Ring has less than 3 points:", ring.length);
      return { dimensions: [], center: null };
    }

    if (!Array.isArray(ring[0])) {
      console.error("Ring[0] is not an array:", ring[0]);
      return { dimensions: [], center: null };
    }

    if (ring[0].length < 2) {
      console.error("Ring[0] does not have at least 2 coordinates:", ring[0]);
      return { dimensions: [], center: null };
    }

  


    const dimensions = [];
    const center = {
      lng: ring.reduce((sum, coord) => sum + coord[0], 0) / ring.length,
      lat: ring.reduce((sum, coord) => sum + coord[1], 0) / ring.length,
    };

    // Calculate distance for each side of the polygon
    for (let i = 0; i < ring.length - 1; i++) {
      const p1 = ring[i];
      const p2 = ring[i + 1];
      const distance = calculateDistance(p1[1], p1[0], p2[1], p2[0]);

      // Calculate midpoint of the side
      const midpoint = {
        lng: (p1[0] + p2[0]) / 2,
        lat: (p1[1] + p2[1]) / 2,
      };

      // Calculate angle for text rotation
      let angle = (Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180) / Math.PI;
      if (angle > 90 || angle < -90) {
        angle += 180;
      }
      dimensions.push({
        distance: distance,
        midpoint: midpoint,
        angle: angle,
        start: { lng: p1[0], lat: p1[1] },
        end: { lng: p2[0], lat: p2[1] },
      });
    }

    return { dimensions, center };
  };

  // Function to show parcel dimensions on map
  const showParcelDimensions = (parcelData) => {
    if (!mapRef.current || !parcelData.coordinates) {
      //   hasMap: !!mapRef.current,
      //   hasCoordinates: !!parcelData.coordinates,
      //   parcelNumber: parcelData.parcel_number,
      // });
      return;
    }


    try {
      // Parse coordinates based on different possible formats
      let coordinates;

      if (typeof parcelData.coordinates === "string") {
        // Parse JSON string
        try {
          const parsed = JSON.parse(parcelData.coordinates);
          coordinates = parsed.coordinates || parsed;
        } catch (parseError) {
          console.error("Failed to parse coordinates string:", parseError);
          return;
        }
      } else if (
        parcelData.coordinates &&
        typeof parcelData.coordinates === "object"
      ) {
        if (parcelData.coordinates.coordinates) {
          // GeoJSON format
          coordinates = parcelData.coordinates.coordinates;
        } else if (Array.isArray(parcelData.coordinates)) {
          // Direct coordinates array
          coordinates = parcelData.coordinates;
        } else {
          // Try to use the object directly
          coordinates = parcelData.coordinates;
        }
      } else {
        console.error(
          "Unknown coordinates format:",
          typeof parcelData.coordinates,
          parcelData.coordinates
        );
        return;
      }


      // Additional validation
      if (!coordinates) {
        console.error("No valid coordinates found after parsing");
        return;
      }

      const { dimensions, center } = calculateParcelDimensions(coordinates);

      if (dimensions.length === 0) {
        console.warn(
          "No dimensions calculated for parcel:",
          parcelData.parcel_number
        );
        return;
      }

      // Ensure a fully clean state before re-adding (covers paths that skipped hideParcelDimensions)
      hideParcelDimensions();

      // Create dimension lines and labels
      const dimensionFeatures = [];
      const lineFeatures = [];

      dimensions.forEach((dim, index) => {
        // Create dimension line
        lineFeatures.push({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [dim.start.lng, dim.start.lat],
              [dim.end.lng, dim.end.lat],
            ],
          },
          properties: {
            type: "dimension-line",
          },
        });

        // Create dimension label
        const distanceText =
          dim.distance < 1000
            ? `${Math.round(dim.distance * 100) / 100}م`
            : `${Math.round(dim.distance / 10) / 100}كم`;

        dimensionFeatures.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [dim.midpoint.lng, dim.midpoint.lat],
          },
          properties: {
            distance: distanceText,
            angle: dim.angle,
            type: "dimension-label",
          },
        });
      });

      // Add dimension lines source and layer
      mapRef.current.addSource("parcel-dimension-lines", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: lineFeatures,
        },
      });

      mapRef.current.addLayer({
        id: "parcel-dimension-lines",
        type: "line",
        source: "parcel-dimension-lines",
        paint: {
          "line-color": "#ff6b35",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            14,
            2,
            18,
            4,
            22,
            6,
          ],
          "line-dasharray": [3, 3],
        },
        minzoom: 14,
      });

      // Add dimensions source and layer
      mapRef.current.addSource("parcel-dimensions", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: dimensionFeatures,
        },
      });

      mapRef.current.addLayer({
        id: "parcel-dimensions",
        type: "symbol",
        source: "parcel-dimensions",
        layout: {
          "text-field": ["get", "distance"],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            14,
            10,
            18,
            14,
            22,
            18,
          ],
          "text-rotate": 0,
          "text-anchor": "center",
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#ff6b35",
          "text-halo-width": 3,
        },
        minzoom: 14,
      });

      //   `✅ Added dimensions for unit ${parcelData.parcel_number}:`,
      //   dimensions.length,
      //   "sides"
      // );
    } catch (error) {
      console.error("Error showing unit dimensions:", error);
      console.error("Parcel data that caused error:", parcelData);
      console.error("Error details:", error.message, error.stack);

      // Don't let dimension errors break the popup functionality
      // Just log the error and continue
    }
  };

  // Function to hide parcel dimensions
  const hideParcelDimensions = () => {
    // Check if map reference exists and is properly initialized
    if (!mapRef.current) {
      return;
    }

    // Check if map has required methods (map might be in process of being destroyed)
    if (typeof mapRef.current.getLayer !== 'function' ||
        typeof mapRef.current.getSource !== 'function' ||
        typeof mapRef.current.removeLayer !== 'function' ||
        typeof mapRef.current.removeSource !== 'function') {
      return;
    }


    const map = mapRef.current;
    const safeRemoveLayer = (id) => {
      try {
        if (map.getLayer(id)) map.removeLayer(id);
      } catch (e) {
        console.warn(`Failed to remove layer ${id}:`, e);
      }
    };
    const safeRemoveSource = (id) => {
      try {
        if (map.getSource(id)) map.removeSource(id);
      } catch (e) {
        console.warn(`Failed to remove source ${id}:`, e);
      }
    };

    // Layers must be removed before their sources
    safeRemoveLayer("parcel-dimensions");
    safeRemoveLayer("parcel-dimension-lines");
    safeRemoveSource("parcel-dimensions");
    safeRemoveSource("parcel-dimension-lines");
  };

  // Function to manage price display layer
  const managePriceDisplayLayer = (
    isPriceFilterActive,
    isStatusFilterActive,
    isUsageFilterActive
  ) => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

    const priceLayerId = "parcels-prices";

    //   isPriceFilterActive,
    //   isStatusFilterActive,
    //   isUsageFilterActive,
    // });

    try {
      // Check if price layer exists
      const layerExists = mapRef.current.getLayer(priceLayerId);

      if (isPriceFilterActive) {
        // Show prices when price filter is active (regardless of other filters)
        if (!layerExists) {
          // Create price display layer
          mapRef.current.addLayer({
            id: priceLayerId,
            type: "symbol",
            source: "parcels",
            layout: {
              "text-field": [
                "format",
                ["get", "parcel_number"],
                { "font-scale": 1.4, "text-color": "#ffffff" },
                "\n",
                {},
                [
                  "number-format",
                  [
                    "case",
                    ["==", ["typeof", ["get", "price_with_tax"]], "string"],
                    ["to-number", ["get", "price_with_tax"]],
                    ["get", "price_with_tax"],
                  ],
                  { locale: "ar" },
                ],
                { "font-scale": 1.0, "text-color": "#626262" },
                " SAR",
                { "font-scale": 1.0, "text-color": "#626262" },
              ],
              "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
              "text-size": 12,
              "text-offset": [0, -2.0],
              "text-anchor": "center",
              //bold
              // "text-weight": "bold",
              
              "text-allow-overlap": false,
              "text-ignore-placement": false,
            },
            paint: {
              "text-color": "#ffffff",
              "text-halo-color": "#000000",
              "text-halo-width": 0,
            },
            // minzoom: 14
          });
        } else {
          // Show existing layer
          mapRef.current.setLayoutProperty(
            priceLayerId,
            "visibility",
            "visible"
          );
        }
      } else {
        // Hide prices when price filter is not active
        if (layerExists) {
          mapRef.current.setLayoutProperty(priceLayerId, "visibility", "none");
        }
      }
    } catch (error) {
      console.error("Error managing price display layer:", error);
    }
  };

  // Function to apply filters to map
  const applyFiltersToMap = (filters) => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
      return;
    }

    // Check if parcels layer exists
    try {
      if (!mapRef.current.getLayer("parcels-fill")) {
        return;
      }
    } catch (error) {
      //   "Error checking parcels layer, skipping filter application:",
      //   error
      // );
      return;
    }

    let filterExpression = ["all"];

    // Check if different filter types are active
    const isPriceFilterActive =
      filters.isPriceFilterActive || isPriceFilterExplicitlyActive;
    const isStatusFilterActive =
      filters.salesStatus && filters.salesStatus.length > 0;
    const isUsageFilterActive = filters.services && filters.services.length > 0;
    const isFloorFilterActive = filters.floors && filters.floors.length > 0;

    // Manage price display layer based on active filters
    managePriceDisplayLayer(
      isPriceFilterActive,
      isStatusFilterActive,
      isUsageFilterActive
    );

    // If no filters are active, remove all filters
    const hasActiveFilters =
      isStatusFilterActive ||
      isUsageFilterActive ||
      isPriceFilterActive ||
      isFloorFilterActive;

    if (!hasActiveFilters) {
      // Remove all filters
      const layers = ["parcels-fill", "parcels-outline", "parcels-labels"];
      layers.forEach((layerId) => {
        if (mapRef.current.getLayer(layerId)) {
          mapRef.current.setFilter(layerId, null);
        }
      });
      return;
    }

    // Sales status filter
    if (filters.salesStatus && filters.salesStatus.length > 0) {
      const statusFilter = [
        "in",
        ["get", "sales_status"],
        ["literal", filters.salesStatus],
      ];
      filterExpression.push(statusFilter);
    }

    // Price range filter - simplified with type conversion
    if (
      filters.priceRange &&
      filters.priceRange.min !== undefined &&
      filters.priceRange.max !== undefined
    ) {
      const priceFilter = [
        "all",
        [
          ">=",
          [
            "case",
            ["==", ["typeof", ["get", "price_with_tax"]], "string"],
            ["to-number", ["get", "price_with_tax"]],
            ["get", "price_with_tax"],
          ],
          filters.priceRange.min,
        ],
        [
          "<=",
          [
            "case",
            ["==", ["typeof", ["get", "price_with_tax"]], "string"],
            ["to-number", ["get", "price_with_tax"]],
            ["get", "price_with_tax"],
          ],
          filters.priceRange.max,
        ],
      ];
      filterExpression.push(priceFilter);
    }

    // Usage type filter - filter by land use type
    if (filters.services && filters.services.length > 0) {

      // Debug: Check what usage types are available in the data
      let availableUsageTypes = [];
      if (mapRef.current && mapRef.current.getSource("parcels")) {
        const source = mapRef.current.getSource("parcels");
        if (source._data && source._data.features) {
          const usageTypes = source._data.features
            .map((f) => f.properties.usage_type)
            .filter(Boolean);
          availableUsageTypes = [...new Set(usageTypes)];
          //   "🏷️ Sample parcel properties:",
          //   source._data.features[0]?.properties
          // );
        }
      }

      // Create mapping for English to Arabic names (for backward compatibility)
      const usageMapping = {
        Residential: "سكني",
        Commercial: "تجاري",
        Educational: "تعليمي",
        Healthcare: "صحي",
      };

      // Check if data uses Arabic names and convert if needed
      const dataUsesArabic = availableUsageTypes.some((type) =>
        Object.values(usageMapping).includes(type)
      );
      let filterValues = filters.services;

      if (dataUsesArabic) {
        filterValues = filters.services.map(
          (service) => usageMapping[service] || service
        );
      } else {
      }

      const usageFilter = [
        "in",
        ["get", "usage_type"],
        ["literal", filterValues],
      ];
      filterExpression.push(usageFilter);
    }

    // Floor filter - only apply if we have floor data loaded from API (not static files)
    if (filters.floors && filters.floors.length > 0) {
      //   "🏢 Note: Floor filtering should be handled by loading specific floor data, not by filtering static data"
      // );

      // For residential projects, floor filtering is handled by loading specific floor data
      // This filter should not be applied to static GeoJSON data
      // The floor data loading in loadFloorData() replaces the entire dataset
    }

    // Apply filter to all parcel layers (including price layer)
    const layers = [
      "parcels-fill",
      "parcels-outline",
      "parcels-labels",
      "parcels-prices",
    ];

    layers.forEach((layerId) => {
      if (mapRef.current.getLayer(layerId)) {
        if (filterExpression.length > 1) {
          mapRef.current.setFilter(layerId, filterExpression);
        } else {
          mapRef.current.setFilter(layerId, null); // Remove filter if no conditions
        }
      }
    });
  };

  // Add floor basemap overlay to map
  const addFloorBasemapOverlay = async (basemapData) => {
    if (
      !mapRef.current ||
      !basemapData.basemap_image_url ||
      !basemapData.basemap_coordinates
    ) {
      
      return;
    }

    try {

      // Remove existing basemap overlay if it exists
      removeFloorBasemapOverlay();

      // Add the basemap image as a source
      const sourceId = "floor-basemap";
      const layerId = "floor-basemap-layer";
      const sourceId3D = "floor-basemap-3D";
      const layerId3D = "floor-basemap-layer-3d";

      // Ensure coordinates are in the correct format [[lng, lat], [lng, lat], [lng, lat], [lng, lat]]
      let coordinates = basemapData.basemap_coordinates;
      if (typeof coordinates === 'string') {
        try { coordinates = JSON.parse(coordinates); } catch(e) {}
      }

      if (!Array.isArray(coordinates) || coordinates.length !== 4) {
        console.error("🗺️ Invalid basemap coordinates format:", coordinates);
        return;
      }

      if (basemapData.basemap_image3D_url && basemapData.basemap_coordinates3d) {
        let coordinates3d = basemapData.basemap_coordinates3d;
        if (typeof coordinates3d === 'string') {
          try { coordinates3d = JSON.parse(coordinates3d); } catch(e) {}
        }

        if (Array.isArray(coordinates3d) && coordinates3d.length === 4) {
          mapRef.current.addSource(sourceId3D, {
            type: "image",
            url: basemapData.basemap_image3D_url,
            coordinates: coordinates3d,
          });
          mapRef.current.addLayer(
            {
              id: layerId3D,
              type: "raster",
              source: sourceId3D,
              paint: {
                "raster-opacity": 0.8,
                "raster-fade-duration": 300,
              },
            },
            "parcels-fill"
          );
        } else {
          console.error("🗺️ Invalid 3d basemap coordinates format:", coordinates3d);
        }
      }

      // Add image source
      mapRef.current.addSource(sourceId, {
        type: "image",
        url: basemapData.basemap_image_url,
        coordinates: coordinates,
      });

      // Add image layer - place it below all other layers
      mapRef.current.addLayer(
        {
          id: layerId,
          type: "raster",
          source: sourceId,
          paint: {
            "raster-opacity": 0.8,
            "raster-fade-duration": 300,
          },
        },
        "parcels-fill"
      ); // Add before parcels layer

    } catch (error) {
      console.error("❌ Error adding floor basemap overlay:", error);
    }
  };

  // Remove floor basemap overlay from map
  const removeFloorBasemapOverlay = () => {
    if (!mapRef.current) return;

    try {
      const sourceId = "floor-basemap";
      const layerId = "floor-basemap-layer";

      // Remove layer if it exists
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // Remove source if it exists
      if (mapRef.current.getSource(sourceId)) {
        mapRef.current.removeSource(sourceId);
      }
    } catch (error) {
      console.error("❌ Error removing floor basemap overlay:", error);
    }

    //remove 3d
    try {
      const sourceId3D = "floor-basemap-3D";
      const layerId3D = "floor-basemap-layer-3d";

      // Remove layer if it exists
      if (mapRef.current.getLayer(layerId3D)) {
        mapRef.current.removeLayer(layerId3D);
      }

      // Remove source if it exists
      if (mapRef.current.getSource(sourceId3D)) {
        mapRef.current.removeSource(sourceId3D);
      }
    } catch (error) {
      console.error("❌ Error removing 3d floor basemap overlay:", error);
    }
  };

  // Add temporary overlay showing units count
  const addUnitsCountOverlay = (unitsCount, floorIds) => {
    // Remove existing overlay
    const existingOverlay = document.getElementById("floor-units-overlay");
    if (existingOverlay) {
      existingOverlay.remove();
    }

    // Create new overlay
    const overlay = document.createElement("div");
    overlay.id = "floor-units-overlay";
    overlay.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 15px 25px;
      border-radius: 25px;
      font-size: 16px;
      font-weight: bold;
      z-index: 1000;
      backdrop-filter: blur(10px);
      border: 2px solid #4fc3f7;
      animation: fadeInOut 3s ease-in-out;
    `;

    const floorId = floorIds[0]; // We only show one floor at a time
    const floor = projectFloors.find((f) => f.id === floorId);
    const floorName = floor
      ? language === "ar"
        ? floor.name_ar || floor.name
        : floor.name_en || floor.name
      : `Floor ${floorId}`;

    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="font-size: 16px; margin-bottom: 5px; opacity: 0.9;">
          ${language === "ar" ? "الطابق المعروض" : "Showing Floor"}
        </div>
        <div style="font-size: 20px; color: #4fc3f7; margin-bottom: 5px; font-weight: bold;">
          ${floorName}
        </div>
        <div style="font-size: 18px; margin-bottom: 5px;">
          ${unitsCount} ${language === "ar" ? "وحدة" : "units"}
        </div>
      </div>
    `;

    // Add CSS animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        20% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(overlay);

    // Remove overlay after animation
    setTimeout(() => {
      if (overlay && overlay.parentNode) {
        overlay.remove();
      }
      if (style && style.parentNode) {
        style.remove();
      }
    }, 3000);
  };

  // Load floor-specific data for residential projects using new service
  const loadFloorData = async (floorIds, project_id) => {
    MapLogger.logOperationStart('load-floor-data', { floorIds, project_id });
    
    if (!floorIds || floorIds.length === 0) {
      MapLogger.logWarning('load-floor-data', 'No floor IDs provided');
      return;
    }

    try {
      // Find project data
      const project = projects.find(p => p.id === project_id);
      if (!project) {
        MapLogger.logError('load-floor-data', 'Project not found', new Error('Project not found'), { project_id });
      return;
    }

      // Use new service to display residential project
      const result = await displayResidentialProject(project_id, floorIds, project);
      
      if (result.success) {
        MapLogger.logOperationEnd('load-floor-data', true, { project_id, floorIds });
      } else {
        MapLogger.logOperationEnd('load-floor-data', false, { error: result.error });
      }
    } catch (error) {
      MapErrorHandler.handleProjectLoadError(error, project_id, { 
        operation: 'load-floor-data',
        floorIds 
      });
      MapLogger.logOperationEnd('load-floor-data', false, { error: error.message });
    }
  };


  // Handle filters change using new service
  const handleFiltersChange = async (newFilters) => {
    MapLogger.logOperationStart('handle-filters-change', { newFilters });

    try {
      // Update price filter state if provided
      if (newFilters.isPriceFilterActive !== undefined) {
        setIsPriceFilterExplicitlyActive(newFilters.isPriceFilterActive);
      }

      setActiveFilters(newFilters);
      
      // Use new service to apply filters
      const result = await applyProjectFilters(newFilters);
      
      if (result.success) {
        MapLogger.logOperationEnd('handle-filters-change', true);
      } else {
        MapLogger.logOperationEnd('handle-filters-change', false, { error: result.error });
      }
    } catch (error) {
      MapErrorHandler.handleFilterError(error, 'general', { newFilters });
      MapLogger.logOperationEnd('handle-filters-change', false, { error: error.message });
    }
  };

  // Apply external filters when they change using new service
  useEffect(() => {
    const applyFiltersWithDelay = async () => {
      try {
        if (filters) {
          setActiveFilters(filters);
          await applyProjectFilters(filters);
        } else {
          // Build filters from individual props
          const combinedFilters = {
            salesStatus: statusFilters,
            priceRange: priceFilter,
            services: usageFilters,
            floors: selectedFloor ? [selectedFloor.id] : [],
          };

          // For residential projects with selectedFloor, load floor-specific data
          if (selectedFloor && selectedProject) {
            const project = projects.find((p) => p.id === selectedProject);
            if (project && project.project_type === "residential") {
              MapLogger.logMapInfo('Loading floor-specific data for residential project', {
                projectId: selectedProject,
                floorId: selectedFloor.id
              });

              if (isMapReady()) {
                await loadFloorData([selectedFloor.id], project.id);
              } else {
                setTimeout(async () => {
                  if (isMapReady()) {
                    await loadFloorData([selectedFloor.id], project.id);
                  } else {
                    MapLogger.logWarning('map-ready', 'Map still not ready after wait');
                  }
                }, 1000);
              }
              return; // Don't apply regular filters, floor data will be loaded instead
            }
          } else {
            // If no floor is selected, remove any existing basemap
            removeFloorBasemapOverlay();
          }

          setActiveFilters(combinedFilters);
          await applyProjectFilters(combinedFilters);
        }
      } catch (error) {
        MapErrorHandler.handleFilterError(error, 'useEffect', { 
          filters, 
          statusFilters, 
          priceFilter, 
          usageFilters, 
          floorFilters 
        });
      }
    };

    // Check if map is ready safely
    const isMapReady = () => {
      try {
        return (
          mapRef.current &&
          mapRef.current.isStyleLoaded &&
          mapRef.current.isStyleLoaded() &&
          mapRef.current.getLayer("parcels-fill")
        );
      } catch (error) {
        return false;
      }
    };

    // Apply filters immediately if map is ready, otherwise wait
    if (isMapReady()) {
      applyFiltersWithDelay();
    } else {
      // Wait for map to be ready
      const timer = setTimeout(() => {
        if (isMapReady()) {
          applyFiltersWithDelay();
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [filters, statusFilters, priceFilter, usageFilters, selectedFloor]);

  // Separate useEffect for selectedFloor to ensure they work even when map is loading
  useEffect(() => {
    const handleSelectedFloorChange = async () => {
            console.log ("handleSelectedFloorChange",selectedFloor,selectedProject)


      if (selectedFloor && selectedProject) {
        const project = projects.find((p) => p.id === selectedProject);

        if (project && project.project_type === "residential") {


          // Wait for map to be ready
          const waitForMap = () => {
            return new Promise((resolve) => {
              const checkMap = () => {
                if (
                  mapRef.current &&
                  mapRef.current.isStyleLoaded &&
                  mapRef.current.isStyleLoaded()
                ) {
                  resolve();
                } else {
                  setTimeout(checkMap, 100);
                }
              };
              checkMap();
            });
          };
          console.log ("handleSelectedFloorChange start map",selectedFloor,project);
          // await waitForMap();
          console.log ("handleSelectedFloorChange finish map",selectedFloor,project);

          loadFloorData([selectedFloor.id], project.id);
        }
      }
    };

    handleSelectedFloorChange();
  }, [selectedFloor, selectedProject]);

  // Function to search for parcel by number or object
  const searchParcel = useCallback(
    async (queryData) => {
      if (!mapRef.current) return;

      try {
        isSearchingUnitRef.current = true; // Block project panel from opening

        let foundParcel = null;
        let parcelNumber = null;

        if (typeof queryData === 'object' && queryData !== null) {
          // If a full unit object is passed, use it directly
          foundParcel = queryData;
          parcelNumber = foundParcel.parcel_number;
        } else {
          // Otherwise, fall back to searching by number
          parcelNumber = queryData;
          
          // Use the search API to find parcels by number, filtering by selected project if available
          const response = await searchParcels(
            parcelNumber,
            {},
            1,
            10,
            "created_at",
            "desc",
            selectedProject
          );
          const parcels = response.data.data;

          if (parcels.length > 0) {
            // Find exact match first, or use the first result
            foundParcel =
              parcels.find(
                (parcel) =>
                  parcel.parcel_number.toString() === parcelNumber.toString()
              ) || parcels[0];
          }
        }

        if (foundParcel) {
          // Check if this is a residential unit with a floor_id
          const hasFloorId = foundParcel.floor_id && foundParcel.floor_id !== null;

          // If the parcel has a floor_id, we need to load that floor first
          if (hasFloorId && selectedFloor) {
            // Check if the parcel's floor is already loaded
            const isFloorLoaded = selectedFloor.id === foundParcel.floor_id;

            if (!isFloorLoaded) {
              // Load the correct floor before displaying the unit
              console.log(`Loading floor ${foundParcel.floor_id} for unit ${foundParcel.parcel_number}`);
              await loadFloorData([foundParcel.floor_id], selectedProject);

              // Wait a bit for the floor data to load
              await new Promise((resolve) => setTimeout(resolve, 1500));
            }
          }

          // Check if the parcel is in the currently loaded map data
          let parcelFeature = mapRef.current.querySourceFeatures("parcels", {
            filter: ["==", "id", foundParcel.id],
          });

          // Wait for map layers to load if the feature is not immediately found
          let retries = 0;
          while (parcelFeature.length === 0 && retries < 10) {
            await new Promise(resolve => setTimeout(resolve, 400));
            if (!mapRef.current) break;

            parcelFeature = mapRef.current.querySourceFeatures("parcels", {
              filter: ["==", "id", foundParcel.id],
            });
            retries++;
          }

          if (parcelFeature.length > 0) {
            // Parcel is already loaded on the map
            const feature = parcelFeature[0];
            try {
              const bounds = new mapboxgl.LngLatBounds();

              // Validate and extend bounds with proper coordinate format
              if (
                feature.geometry &&
                feature.geometry.coordinates &&
                feature.geometry.coordinates[0]
              ) {
                feature.geometry.coordinates[0].forEach((coord) => {
                  // Ensure coord is a valid array with two numbers
                  if (
                    Array.isArray(coord) &&
                    coord.length >= 2 &&
                    typeof coord[0] === "number" &&
                    typeof coord[1] === "number" &&
                    !isNaN(coord[0]) &&
                    !isNaN(coord[1])
                  ) {
                    bounds.extend(coord);
                  } else {
                    
                  }
                });
              }

              // Check if bounds is valid before fitting
              if (!bounds.isEmpty()) {
                // Select the parcel
                setSelectedParcelId(foundParcel.id);

                // Calculate popup position at the bottom center of the parcel
                const center = bounds.getCenter();
                const southwest = bounds.getSouthWest();
                const southeast = bounds.getSouthEast();
                // Use the southernmost point (lowest latitude) with center longitude
                const bottomLat = Math.min(southwest.lat, southeast.lat);
                const popupCoordinates = [center.lng, bottomLat];

                // Add geometry coordinates to parcel data for dimension calculation
                const parcelDataWithCoords = {
                  ...foundParcel,
                  coordinates: feature.geometry,
                };

                // Set up moveend handler to show popup and dimensions after map movement
                const handleMoveEnd = async () => {
                  // Remove the moveend listener to prevent multiple calls
                  mapRef.current.off("moveend", handleMoveEnd);

                  // If onParcelSelect is available, use it to open the SearchResults panel
                  if (onParcelSelect) {
                    handleParcelClick(parcelDataWithCoords);
                  } else {
                    // Fallback: open old modal
                    await createParcelModal(
                      parcelDataWithCoords,
                      popupCoordinates
                    );
                  }
                  isSearchingUnitRef.current = false; // Reset search flag
                };

                // Add moveend listener
                mapRef.current.on("moveend", handleMoveEnd);

                // Start the map movement
                const mapContainer = mapRef.current.getContainer();
                const mapHeight = mapContainer.offsetHeight;
                const offsetY = mapHeight * 0.1; // 10% offset upward

                // Determine max zoom based on project type
                const parcelProjectId =
                  foundParcel.block?.project_id || selectedProject;
                const currentProject = projects.find(
                  (p) => p.id === parcelProjectId
                );
                const maxZoom =
                  currentProject &&
                  currentProject.project_type === "residential"
                    ? 20
                    : 18.5;

                mapRef.current.fitBounds(bounds, {
                  padding: 50,
                  maxZoom: maxZoom,
                  offset: [0, -offsetY], // Negative Y moves map up
                });
              } else {
                console.error("Invalid bounds for parcel feature");
              }
            } catch (error) {
              console.error("Error creating bounds for parcel feature:", error);
            }
          } else {
            // Parcel is not currently loaded - check if it's in a different project or floor
            const parcelProjectId = foundParcel.block?.project_id;
            const parcelFloorId = foundParcel.floor_id;

            // If the parcel has a floor_id, try loading that floor first
            if (parcelFloorId && selectedProject === parcelProjectId) {
              console.log(`Loading floor ${parcelFloorId} for unit ${foundParcel.parcel_number}`);
              await loadFloorData([parcelFloorId], selectedProject);

              // Wait for the floor data to load
              await new Promise((resolve) => setTimeout(resolve, 1500));

              // Try to find the parcel again after loading the floor
              const parcelFeatureAfterFloorLoad = mapRef.current.querySourceFeatures("parcels", {
                filter: ["==", "id", foundParcel.id],
              });

              if (parcelFeatureAfterFloorLoad.length > 0) {
                // Parcel is now loaded, process it
                const feature = parcelFeatureAfterFloorLoad[0];
                try {
                  const bounds = new mapboxgl.LngLatBounds();

                  // Validate and extend bounds with proper coordinate format
                  if (
                    feature.geometry &&
                    feature.geometry.coordinates &&
                    feature.geometry.coordinates[0]
                  ) {
                    feature.geometry.coordinates[0].forEach((coord) => {
                      // Ensure coord is a valid array with two numbers
                      if (
                        Array.isArray(coord) &&
                        coord.length >= 2 &&
                        typeof coord[0] === "number" &&
                        typeof coord[1] === "number" &&
                        !isNaN(coord[0]) &&
                        !isNaN(coord[1])
                      ) {
                        bounds.extend(coord);
                      }
                    });
                  }

                  // Check if bounds is valid before fitting
                  if (!bounds.isEmpty()) {
                    // Select the parcel
                    setSelectedParcelId(foundParcel.id);

                    // Calculate popup position at the bottom center of the parcel
                    const center = bounds.getCenter();
                    const southwest = bounds.getSouthWest();
                    const southeast = bounds.getSouthEast();
                    // Use the southernmost point (lowest latitude) with center longitude
                    const bottomLat = Math.min(southwest.lat, southeast.lat);
                    const popupCoordinates = [center.lng, bottomLat];

                    // Add geometry coordinates to parcel data for dimension calculation
                    const parcelDataWithCoords = {
                      ...foundParcel,
                      coordinates: feature.geometry,
                    };

                    // Set up moveend handler to show popup and dimensions after map movement
                    const handleMoveEnd = async () => {
                      // Remove the moveend listener to prevent multiple calls
                      mapRef.current.off("moveend", handleMoveEnd);

                      // If onParcelSelect is available, use it to open the SearchResults panel
                      if (onParcelSelect) {
                        handleParcelClick(parcelDataWithCoords);
                      } else {
                        await createParcelModal(
                          parcelDataWithCoords,
                          popupCoordinates
                        );
                      }
                      isSearchingUnitRef.current = false; // Reset search flag
                    };

                    // Add moveend listener
                    mapRef.current.on("moveend", handleMoveEnd);

                    // Start the map movement
                    const mapContainer = mapRef.current.getContainer();
                    const mapHeight = mapContainer.offsetHeight;
                    const offsetY = mapHeight * 0.1; // 10% offset upward

                    // Determine max zoom based on project type
                    const currentProject = projects.find(
                      (p) => p.id === selectedProject
                    );
                    const maxZoom =
                      currentProject &&
                      currentProject.project_type === "residential"
                        ? 20
                        : 18.5;

                    mapRef.current.fitBounds(bounds, {
                      padding: 50,
                      maxZoom: maxZoom,
                      offset: [0, -offsetY], // Negative Y moves map up
                    });
                  } else {
                    console.error("Invalid bounds for parcel feature after floor load");
                  }
                } catch (error) {
                  console.error("Error creating bounds for parcel feature after floor load:", error);
                }
                return; // Exit early since we've handled the parcel
              }

              // querySourceFeatures may still come up empty if the floor filter
              // is still being applied. Fall back to the geometry attached on
              // the parcel record so we can still pan + highlight the unit.
              if (foundParcel.coordinates) {
                try {
                  const geometry =
                    typeof foundParcel.coordinates === 'string'
                      ? JSON.parse(foundParcel.coordinates)
                      : foundParcel.coordinates;
                  const bounds = new mapboxgl.LngLatBounds();
                  const extendBoundsFromCoords = (node) => {
                    if (!Array.isArray(node)) return;
                    if (
                      node.length >= 2 &&
                      typeof node[0] === 'number' &&
                      typeof node[1] === 'number' &&
                      !isNaN(node[0]) &&
                      !isNaN(node[1])
                    ) {
                      bounds.extend(node);
                      return;
                    }
                    node.forEach(extendBoundsFromCoords);
                  };
                  if (geometry && geometry.coordinates) {
                    extendBoundsFromCoords(geometry.coordinates);
                  }

                  if (!bounds.isEmpty()) {
                    setSelectedParcelId(foundParcel.id);
                    const center = bounds.getCenter();
                    const southwest = bounds.getSouthWest();
                    const southeast = bounds.getSouthEast();
                    const bottomLat = Math.min(southwest.lat, southeast.lat);
                    const popupCoordinates = [center.lng, bottomLat];
                    const parcelDataWithCoords = {
                      ...foundParcel,
                      coordinates: geometry,
                    };

                    const handleMoveEnd = async () => {
                      mapRef.current.off("moveend", handleMoveEnd);
                      if (onParcelSelect) {
                        handleParcelClick(parcelDataWithCoords);
                      } else {
                        await createParcelModal(parcelDataWithCoords, popupCoordinates);
                      }
                      isSearchingUnitRef.current = false;
                    };
                    mapRef.current.on("moveend", handleMoveEnd);

                    const mapContainer = mapRef.current.getContainer();
                    const offsetY = mapContainer.offsetHeight * 0.1;
                    const currentProject = projects.find(
                      (p) => p.id === selectedProject
                    );
                    const maxZoom =
                      currentProject &&
                      currentProject.project_type === "residential"
                        ? 20
                        : 18.5;
                    mapRef.current.fitBounds(bounds, {
                      padding: 50,
                      maxZoom,
                      offset: [0, -offsetY],
                    });
                    return;
                  }
                } catch (fallbackError) {
                  console.error(
                    "Error using parcel geometry fallback after floor load:",
                    fallbackError
                  );
                }
              }
            }

            if (
              parcelProjectId &&
              parcelProjectId !== selectedProject &&
              onProjectChange
            ) {
              // Switch to the correct project automatically
              //   language === "ar"
              //     ? "تحميل المشروع الصحيح..."
              //     : "Loading correct project..."
              // );

              // Change the project
              onProjectChange(parcelProjectId);

              // Wait a bit for the project to load, then try to find the parcel again
              setTimeout(async () => {
                try {
                  // Try to find the parcel in the newly loaded data
                  const parcelFeature = mapRef.current.querySourceFeatures(
                    "parcels",
                    {
                      filter: ["==", "id", foundParcel.id],
                    }
                  );

                  if (parcelFeature.length > 0) {
                    // Parcel is now loaded on the map
                    const feature = parcelFeature[0];
                    try {
                      const bounds = new mapboxgl.LngLatBounds();

                      // Validate and extend bounds with proper coordinate format
                      if (
                        feature.geometry &&
                        feature.geometry.coordinates &&
                        feature.geometry.coordinates[0]
                      ) {
                        feature.geometry.coordinates[0].forEach((coord) => {
                          // Ensure coord is a valid array with two numbers
                          if (
                            Array.isArray(coord) &&
                            coord.length >= 2 &&
                            typeof coord[0] === "number" &&
                            typeof coord[1] === "number" &&
                            !isNaN(coord[0]) &&
                            !isNaN(coord[1])
                          ) {
                            bounds.extend(coord);
                          } else {
                            
                          }
                        });
                      }

                      // Check if bounds is valid before fitting
                      if (!bounds.isEmpty()) {
                        // Select the parcel
                        setSelectedParcelId(foundParcel.id);

                        // Calculate popup position at the bottom center of the parcel
                        const center = bounds.getCenter();
                        const southwest = bounds.getSouthWest();
                        const southeast = bounds.getSouthEast();
                        // Use the southernmost point (lowest latitude) with center longitude
                        const bottomLat = Math.min(
                          southwest.lat,
                          southeast.lat
                        );
                        const popupCoordinates = [center.lng, bottomLat];

                        // Attach geometry so dimensions can be computed for the searched unit
                        const parcelDataWithCoords = {
                          ...foundParcel,
                          coordinates: feature.geometry,
                        };

                        // Set up moveend handler to show popup and dimensions after map movement
                        const handleMoveEnd = async () => {
                          // Remove the moveend listener to prevent multiple calls
                          mapRef.current.off("moveend", handleMoveEnd);

                          // If onParcelSelect is available, use it to open the SearchResults panel
                          if (onParcelSelect) {
                            handleParcelClick(parcelDataWithCoords);
                          } else {
                            await createParcelModal(
                              parcelDataWithCoords,
                              popupCoordinates
                            );
                          }
                          isSearchingUnitRef.current = false; // Reset search flag
                        };

                        // Add moveend listener
                        mapRef.current.on("moveend", handleMoveEnd);

                        // Start the map movement
                        const mapContainer = mapRef.current.getContainer();
                        const mapHeight = mapContainer.offsetHeight;
                        const offsetY = mapHeight * 0.1; // 10% offset upward

                        // Determine max zoom based on project type
                        const parcelProjectId =
                          foundParcel.block?.project_id || selectedProject;
                        const currentProject = projects.find(
                          (p) => p.id === parcelProjectId
                        );
                        const maxZoom =
                          currentProject &&
                          currentProject.project_type === "residential"
                            ? 20
                            : 18.5;

                        mapRef.current.fitBounds(bounds, {
                          padding: 50,
                          maxZoom: maxZoom,
                          offset: [0, -offsetY], // Negative Y moves map up
                        });
                      } else {
                        console.error(
                          "Invalid bounds for parcel feature after project change"
                        );
                      }
                    } catch (error) {
                      console.error(
                        "Error creating bounds for parcel feature after project change:",
                        error
                      );
                    }
                  } else {
                    // Fallback to coordinates if still not found
                    if (foundParcel.coordinates) {
                      const geometry = JSON.parse(foundParcel.coordinates);

                      try {
                        const bounds = new mapboxgl.LngLatBounds();

                        // Validate and extend bounds with proper coordinate format
                        if (
                          geometry &&
                          geometry.coordinates &&
                          geometry.coordinates[0]
                        ) {
                          geometry.coordinates[0].forEach((coord) => {
                            // Ensure coord is a valid array with two numbers
                            if (
                              Array.isArray(coord) &&
                              coord.length >= 2 &&
                              typeof coord[0] === "number" &&
                              typeof coord[1] === "number" &&
                              !isNaN(coord[0]) &&
                              !isNaN(coord[1])
                            ) {
                              bounds.extend(coord);
                            } else {
                              
                            }
                          });
                        }

                        // Check if bounds is valid before fitting
                        if (!bounds.isEmpty()) {
                          // Show parcel details with popup
                          setSelectedParcelId(foundParcel.id);

                          // Calculate popup position at the bottom center of the parcel
                          const center = bounds.getCenter();
                          const southwest = bounds.getSouthWest();
                          const southeast = bounds.getSouthEast();
                          // Use the southernmost point (lowest latitude) with center longitude
                          const bottomLat = Math.min(
                            southwest.lat,
                            southeast.lat
                          );
                          const popupCoordinates = [center.lng, bottomLat];

                          // Attach parsed geometry so dimensions can be computed for the searched unit
                          const parcelDataWithCoords = {
                            ...foundParcel,
                            coordinates: geometry,
                          };

                          // Set up moveend handler to show popup and dimensions after map movement
                          const handleMoveEnd = async () => {
                            // Remove the moveend listener to prevent multiple calls
                            mapRef.current.off("moveend", handleMoveEnd);

                            // If onParcelSelect is available, use it to open the SearchResults panel
                            if (onParcelSelect) {
                              handleParcelClick(parcelDataWithCoords);
                            } else {
                              await createParcelModal(
                                parcelDataWithCoords,
                                popupCoordinates
                              );
                            }
                            isSearchingUnitRef.current = false; // Reset search flag
                          };

                          // Add moveend listener
                          mapRef.current.on("moveend", handleMoveEnd);

                          // Start the map movement
                          const mapContainer = mapRef.current.getContainer();
                          const mapHeight = mapContainer.offsetHeight;
                          const offsetY = mapHeight * 0.1; // 10% offset upward

                          // Determine max zoom based on project type
                          const parcelProjectId =
                            foundParcel.block?.project_id || selectedProject;
                          const currentProject = projects.find(
                            (p) => p.id === parcelProjectId
                          );
                          const maxZoom =
                            currentProject &&
                            currentProject.project_type === "residential"
                              ? 20
                              : 18.5;

                          mapRef.current.fitBounds(bounds, {
                            padding: 50,
                            maxZoom: maxZoom,
                            offset: [0, -offsetY], // Negative Y moves map up
                          });
                        } else {
                          console.error(
                            "Invalid bounds for parsed parcel coordinates"
                          );
                        }
                      } catch (boundsError) {
                        console.error(
                          "Error creating bounds for parsed parcel coordinates:",
                          boundsError
                        );
                      }
                    }
                  }
                } catch (error) {
                  console.error(
                    "Error finding parcel after project change:",
                    error
                  );
                }
              }, 3000); // Wait 3 seconds for project data to load
            } else {
              // Try to zoom using coordinates directly
              if (foundParcel.coordinates) {
                try {
                  const geometry =
                    typeof foundParcel.coordinates === 'string'
                      ? JSON.parse(foundParcel.coordinates)
                      : foundParcel.coordinates;

                  try {
                    const bounds = new mapboxgl.LngLatBounds();

                    // Walk arbitrary coordinate nesting so Polygon, MultiPolygon,
                    // LineString, and Point geometries all extend the bounds.
                    const extendBoundsFromCoords = (node) => {
                      if (!Array.isArray(node)) return;
                      if (
                        node.length >= 2 &&
                        typeof node[0] === 'number' &&
                        typeof node[1] === 'number' &&
                        !isNaN(node[0]) &&
                        !isNaN(node[1])
                      ) {
                        bounds.extend(node);
                        return;
                      }
                      node.forEach(extendBoundsFromCoords);
                    };
                    if (geometry && geometry.coordinates) {
                      extendBoundsFromCoords(geometry.coordinates);
                    }

                    // Check if bounds is valid before fitting
                    if (!bounds.isEmpty()) {
                      // Show parcel details with popup
                      setSelectedParcelId(foundParcel.id);

                      // Calculate popup position at the bottom center of the parcel
                      const center = bounds.getCenter();
                      const southwest = bounds.getSouthWest();
                      const southeast = bounds.getSouthEast();
                      // Use the southernmost point (lowest latitude) with center longitude
                      const bottomLat = Math.min(southwest.lat, southeast.lat);
                      const popupCoordinates = [center.lng, bottomLat];

                      // Attach parsed geometry so dimensions can be computed for the searched unit
                      const parcelDataWithCoords = {
                        ...foundParcel,
                        coordinates: geometry,
                      };

                      // Set up moveend handler to show popup and dimensions after map movement
                      const handleMoveEnd = async () => {
                        // Remove the moveend listener to prevent multiple calls
                        mapRef.current.off("moveend", handleMoveEnd);

                        // If onParcelSelect is available, use it to open the SearchResults panel
                        if (onParcelSelect) {
                          handleParcelClick(parcelDataWithCoords);
                        } else {
                          await createParcelModal(parcelDataWithCoords, popupCoordinates);
                        }
                        isSearchingUnitRef.current = false; // Reset search flag
                      };

                      // Add moveend listener
                      mapRef.current.on("moveend", handleMoveEnd);

                      // Start the map movement
                      const mapContainer = mapRef.current.getContainer();
                      const mapHeight = mapContainer.offsetHeight;
                      const offsetY = mapHeight * 0.1; // 10% offset upward

                      // Determine max zoom based on project type
                      const parcelProjectId =
                        foundParcel.block?.project_id || selectedProject;
                      const currentProject = projects.find(
                        (p) => p.id === parcelProjectId
                      );
                      const maxZoom =
                        currentProject &&
                        currentProject.project_type === "residential"
                          ? 20
                          : 18.5;

                      mapRef.current.fitBounds(bounds, {
                        padding: 50,
                        maxZoom: maxZoom,
                        offset: [0, -offsetY], // Negative Y moves map up
                      });
                    } else {
                      console.error(
                        "Invalid bounds for fallback parcel coordinates"
                      );
                    }
                  } catch (boundsError) {
                    console.error(
                      "Error creating bounds for fallback parcel coordinates:",
                      boundsError
                    );
                  }
                } catch (coordError) {
                  console.error(
                    "Error parsing parcel coordinates:",
                    coordError
                  );
                  alert(
                    language === "ar"
                      ? `تم العثور على الوحدة ولكن لا يمكن عرضها على الخريطة`
                      : `Parcel found but cannot be displayed on map`
                  );
                }
              } else {
                alert(
                  language === "ar"
                    ? `تم العثور على الوحدة ولكن لا يمكن عرضها على الخريطة`
                    : `Parcel found but cannot be displayed on map`
                );
              }
            }
          }
        } else {
          // Show error message if parcel not found
          alert(
            language === "ar"
              ? `لم يتم العثور على وحدة برقم: ${parcelNumber}`
              : `Parcel not found with number: ${parcelNumber}`
          );
        }
        
        // Ensure flag is reset if no parcel was found or operations failed early
        isSearchingUnitRef.current = false;
      } catch (error) {
        console.error("Error searching for parcel:", error);
        alert(
          language === "ar"
            ? "حدث خطأ أثناء البحث عن الوحدة"
            : "Error occurred while searching for parcel"
        );
        isSearchingUnitRef.current = false;
        
        // Fallback: show normal project details if search failed
        if (selectedProjectData) {
          setIsProjectPanelVisible(true);
        }
      }
    },
    [language, selectedProject, onProjectChange, projects, selectedFloor]
  );

  // Helper function to render popup content
  const renderPopupContent = (
    parcelData,
    isLoadingFavorite = false,
    favoriteStatus = null
  ) => {
    if (!popupRootRef.current || !popupRef.current) {
      return;
    }

    const projectData = selectedProject
      ? projects.find((p) => p.id === selectedProject)
      : projects[0];

    // Get the current parcel data (either detailed or basic)
    const currentParcelData = selectedParcelData || parcelData;

    // Determine favorite status
    const isFavorited =
      favoriteStatus !== null
        ? favoriteStatus
        : currentParcelData.is_favorited ||
          parcelFavoriteStatus[parcelData.id] ||
          false;

    // popupRootRef.current.render(
    //   React.createElement(ParcelPopupContent, {
    //     parcelData: currentParcelData,
    //     projectData: projectData,
    //     language: language,
    //     isLoading: false,
    //     isFavorited: isFavorited,
    //     isLoadingFavorite:
    //       isLoadingFavorite || favoriteLoadingStatus[parcelData.id] || false,
    //     onVirtualTour: (parcelData) => {
    //     },
    //     onShare: (parcelData) => {
    //     },
    //     onReserve: onReserve
    //       ? (parcelData) => {
    //           onReserve(parcelData, projectData);
    //         }
    //       : (parcelData) => {
    //         },
    //     onToggleFavorite: handleToggleFavorite,
    //   })
    // );
    //   "✅ Popup rendered with favorite status:",
    //   isFavorited,
    //   "loading:",
    //   isLoadingFavorite
    // );
  };

  // Handle favorite toggle
  const handleToggleFavorite = async (parcelData) => {
    try {

      // Set loading state
      setFavoriteLoadingStatus((prev) => ({
        ...prev,
        [parcelData.id]: true,
      }));

      // Re-render popup with loading state
      if (popupRef.current && activeParcelRef.current === parcelData.id) {
        renderPopupContent(parcelData, true); // true = isLoadingFavorite
      }

      const response = await toggleFavorite(parcelData.id);

      if (response.data.success) {
        // Update local state
        setParcelFavoriteStatus((prev) => ({
          ...prev,
          [parcelData.id]: response.data.is_favorited,
        }));


        // Re-render the popup with updated favorite status
        if (popupRef.current && activeParcelRef.current === parcelData.id) {
          renderPopupContent(parcelData, false, response.data.is_favorited); // false = not loading, with new status
          //   "✅ Re-rendered popup with favorite status:",
          //   response.data.is_favorited
          // );
        }
      } else {
        console.error("Failed to toggle favorite:", response.data.message);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      if (error.response?.status === 401) {
        alert(
          language === "ar" ? "يجب تسجيل الدخول أولاً" : "Please login first"
        );
      }

      // Re-render popup to remove loading state in case of error
      if (popupRef.current && activeParcelRef.current === parcelData.id) {
        renderPopupContent(parcelData, false); // false = not loading, keep original status
      }
    } finally {
      // Clear loading state
      setFavoriteLoadingStatus((prev) => ({
        ...prev,
        [parcelData.id]: false,
      }));

      // Don't re-render here as it will override the success state
      // The success case already handles the re-render with correct status
    }
  };

  // Add refs for better popup management
  const activeParcelRef = useRef(null);
  const apiAbortControllerRef = useRef(null);
  const lastClickTimeRef = useRef(0);
  const popupRootRef = useRef(null);

  const createParcelModal = async (parcelData, coordinates) => {
    // Cancel any previous API call

    if (apiAbortControllerRef.current) {
      apiAbortControllerRef.current.abort();
    }

    // Always close any existing popup first
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    // Hide any existing dimensions before showing new ones
    hideParcelDimensions();

    // Set active parcel immediately to prevent duplicate calls
    activeParcelRef.current = parcelData.id;

    // Show parcel dimensions immediately since map movement is already complete
    // Only show dimensions if we have valid coordinate data
    if (parcelData.coordinates) {
      //   "Attempting to show dimensions for parcel:",
      //   parcelData.parcel_number
      // );
      showParcelDimensions(parcelData);
    } else {
      //   "No coordinates available for parcel dimensions:",
      //   parcelData.parcel_number
      // );
    }

    // Find project data (for immediate display)
    const projectData = selectedProject
      ? projects.find((p) => p.id === selectedProject)
      : projects[0];

    //   "🚀 Creating modal immediately with available data for:",
    //   parcelData.parcel_number
    // );

    // 1️⃣ IMMEDIATE DISPLAY: Show modal with available data right away
    setSelectedParcelModalData(parcelData);
    setParcelModalLoading(true);
    setParcelModalError(false);
    setIsParcelModalVisible(true);
    // Update state immediately
    setSelectedParcelId(parcelData.id);
    setSelectedParcelData(parcelData);
    setIsParcelPopoverVisible(true);
    setIsUnitDetailsVisible(true);
    
    // Force map styling update to ensure the unit stays highlighted
    // even if it was just loaded asynchronously
    setTimeout(() => {
      updateMapStyling();
    }, 100);
    
    // Hide project details panel when unit details are shown
    setIsProjectPanelVisible(false);

    // 2️⃣ BACKGROUND API CALL: Fetch detailed data and update modal
    const abortController = new AbortController();
    apiAbortControllerRef.current = abortController;

    try {
      //   "📡 Fetching detailed parcel data in background for:",
      //   parcelData.parcel_number
      // );

      const response = await getParcelWithProject(parcelData.id, {
        signal: abortController.signal,
      });

      // Check if this request was cancelled
      if (abortController.signal.aborted) {
        return;
      }

      const detailedParcelData = response.data.data;


      // Enhanced project data from API
      const enhancedProjectData = detailedParcelData.project || projectData;


      // Check if this modal is still the active one before updating
      if (activeParcelRef.current === parcelData.id && isParcelModalVisible) {

        // Update the modal content with detailed data
        setSelectedParcelModalData(detailedParcelData);
        setParcelModalLoading(false);
        setParcelModalError(false);

        // Update favorite status from API response
        if (detailedParcelData.is_favorited !== undefined) {
          setParcelFavoriteStatus((prev) => ({
            ...prev,
            [detailedParcelData.id]: detailedParcelData.is_favorited,
          }));
          //   "✅ Updated favorite status from API:",
          //   detailedParcelData.is_favorited
          // );
        } else {
        }

        // Update state with detailed data
        setSelectedParcelData(detailedParcelData);
      } else {
      }
    } catch (error) {
      // Don't show error for cancelled requests
      if (error.name === "AbortError" || abortController.signal.aborted) {
        return;
      }

      console.error("❌ Error fetching detailed parcel data:", error);

      // Update to remove loading state even if API fails
      if (activeParcelRef.current === parcelData.id && isParcelModalVisible) {
        setParcelModalLoading(false);
        setParcelModalError(true);
      }
    } finally {
      // Clear the abort controller reference if it's this one
      if (apiAbortControllerRef.current === abortController) {
        apiAbortControllerRef.current = null;
      }
    }
  };

  // Pass search function to parent
  useEffect(() => {
    if (onParcelSearch && onParcelSearch.current !== undefined) {
      onParcelSearch.current = searchParcel;
    }
  }, [onParcelSearch, language, searchParcel]);

  // Function to create project marker with developer logo
  const createProjectMarker = (project, coordinates) => {
    if (!mapRef.current) return null;

    const markerContainer = document.createElement("div");
    markerContainer.style.display = "flex";
    markerContainer.style.flexDirection = "column";
    markerContainer.style.alignItems = "center";
    markerContainer.style.cursor = "pointer";
    // Hide until Mapbox applies its translate() — prevents the brief flash at (0,0).
    markerContainer.style.visibility = "hidden";

    // Get status color from project data
    const statusColor = project.status_color || "#22c55e"; // Default to green if no status color

    // Create logo container
    const logoContainer = document.createElement("div");
    logoContainer.style.width = "60px";
    logoContainer.style.height = "60px";
    logoContainer.style.borderRadius = "50%";
    // logoContainer.style.backgroundColor = statusColor;
    // logoContainer.style.border = selectedProject === project.id ? `4px solid ${statusColor}` : `2px solid ${statusColor}`;
    // logoContainer.style.boxShadow = selectedProject === project.id ? `0 6px 16px ${statusColor}40` : "0 4px 8px rgba(0,0,0,0.3)";
    logoContainer.style.display = "flex";
    logoContainer.style.alignItems = "center";
    logoContainer.style.justifyContent = "center";
    logoContainer.style.overflow = "hidden";
    logoContainer.style.transition = "all 0.3s ease";

    // Use project logo first, then fallback to developer logo
    const logoUrl = project.project_logo_url || project.developer_logo_url;

    if (logoUrl) {
      // If project logo or developer logo exists, use it
      const logoImg = document.createElement("img");
      logoImg.src = logoUrl;
      logoImg.style.width = "60px";
      logoImg.style.height = "60px";
      // logoImg.style.borderRadius = "50%";
      logoImg.style.objectFit = "contain";
      logoImg.onerror = () => {
        // Fallback to default marker if image fails to load
        logoContainer.innerHTML = "";
        const fallbackMarker = document.createElement("div");
        fallbackMarker.style.width = "50px";
        fallbackMarker.style.height = "50px";
        fallbackMarker.style.borderRadius = "50%";

        fallbackMarker.style.backgroundColor = statusColor;
        logoContainer.appendChild(fallbackMarker);
      };
      logoContainer.appendChild(logoImg);
    } else {
      // Default marker if no logo
      const defaultMarker = document.createElement("div");
      defaultMarker.style.width = "50px";
      defaultMarker.style.height = "50px";
      defaultMarker.style.borderRadius = "50%";
      defaultMarker.style.backgroundColor = statusColor;
      logoContainer.appendChild(defaultMarker);
    }

    // Create project label
    const markerLabel = document.createElement("div");
    const projectName =
      language === "ar"
        ? project.name_ar || project.name
        : project.name_en || project.name;
    markerLabel.textContent = projectName || "Project";
    markerLabel.style.marginTop = "0px";
    markerLabel.style.fontSize = "12px";
    markerLabel.style.fontWeight = "bold";
    markerLabel.style.color = "#ffffff";
    markerLabel.style.backgroundColor = "rgba(255,255,255,0.95)";
    markerLabel.style.padding = "4px 8px";
    markerLabel.style.borderRadius = "6px";
    markerLabel.style.border = `1px solid ${statusColor}`;
    markerLabel.style.whiteSpace = "nowrap";
    markerLabel.style.boxShadow = "0 2px 4px rgba(0,0,0,0.2)";
    markerLabel.style.backgroundColor = statusColor;
    markerContainer.appendChild(logoContainer);
    markerContainer.appendChild(markerLabel);

    // Create Mapbox marker
    const marker = new mapboxgl.Marker(markerContainer)
      .setLngLat(coordinates)
      .addTo(mapRef.current);

    // Reveal on next frame — Mapbox has set transform: translate(x, y) by then.
    requestAnimationFrame(() => {
      markerContainer.style.visibility = "visible";
    });

    // Add click event
    markerContainer.addEventListener("click", async () => {
      try {
        // Mark that project was clicked directly
        projectClickedDirectlyRef.current = true;
        
        // Update selected project if different
        if (selectedProject !== project.id && onProjectChange) {
          onProjectChange(project.id);
        }

        // Fly to project location first with offset
        // Use zoom level from database or default based on project type
        const zoomLevel = project.map_zoom_level !== null && project.map_zoom_level !== undefined 
          ? project.map_zoom_level 
          : (project.project_type === "residential" ? 18 : 14);
        const flyToOptions = {
          center: coordinates,
          zoom: zoomLevel,
          essential: false,
        };

        // // Add 20% upward offset to the zoom
        // const currentCenter = mapRef.current.getCenter();
        // const offsetLat = (coordinates[1] - currentCenter.lat) * 0.03;
        // flyToOptions.center = [coordinates[0], coordinates[1] - offsetLat];

        // // Start flying to location
        mapRef.current.flyTo(flyToOptions);

        // Wait for the fly animation to complete before showing project details
        mapRef.current.once("moveend", async () => {
          try {
            // Load landmarks for the clicked project
            drowLandMark(project.id).catch((error) => {
              console.error(
                "❌ Error loading landmarks for clicked project:",
                error
              );
            });

            // Add parcels source and layers for the clicked project
            addParcelsSourceAndLayers(project.id);

            // Use pre-loaded project data if statistics are available, otherwise fetch
            if (
              project.statistics &&
              Object.keys(project.statistics).length > 0
            ) {
              if (onProjectSelect) {
                onProjectSelect(project);
              } else {
                setSelectedProjectData(project);
                setIsProjectPanelVisible(true);
              }
            } else {
              // Fetch detailed project data with statistics
              const response = await getProject(project.id);
              const projectData = response.data.data;

              if (onProjectSelect) {
                onProjectSelect(projectData);
              } else {
                setSelectedProjectData(projectData);
                setIsProjectPanelVisible(true);
              }
            }
          } catch (error) {
            console.error("Error fetching project details:", error);
            // Fallback: use basic project data
            if (!isSearchingUnitRef.current) {
              if (onProjectSelect) {
                onProjectSelect(project);
              } else {
                setSelectedProjectData(project);
                setIsProjectPanelVisible(true);
              }
            }
          }
        });
      } catch (error) {
        console.error("Error handling project click:", error);
        // Fallback: use basic project data and fly to location
        if (!isSearchingUnitRef.current) {
          if (onProjectSelect) {
            onProjectSelect(project);
          } else {
            setSelectedProjectData(project);
            setIsProjectPanelVisible(true);
          }
        }
        // Use zoom level from database or default based on project type
        const zoomLevel = project.map_zoom_level !== null && project.map_zoom_level !== undefined
          ? project.map_zoom_level
          : (project.project_type === "residential" ? 18 : 14);
        mapRef.current.flyTo({ center: coordinates, zoom: zoomLevel });
      }
    });

    return marker;
  };

  // Function to filter projects based on status and city
  const filterProjects = (projectsData) => {
    if (!projectsData || projectsData.length === 0) {
      return [];
    }

    
    let filteredProjects = projectsData;

    // Apply status filter
    if (
      projectFilters &&
      projectFilters.status &&
      projectFilters.status.length > 0
    ) {
      
      filteredProjects = filteredProjects.filter((project) => {
        const match = projectFilters.status.includes(project.status);
        if (!match) {
          
        } else {
          
        }
        return match;
      });
      
    }

    // Apply city filter
    if (projectFilters && projectFilters.city && projectFilters.city !== "") {
      
      filteredProjects = filteredProjects.filter(
        (project) => project.city_id === parseInt(projectFilters.city)
      );
      
    }

    
    return filteredProjects;
  };

  // Function to update project markers - Show ALL projects or only shared project
  const updateProjectMarkers = (projectsData) => {
    if (!mapRef.current || !projectsData || projectsData.length === 0) {
      return;
    }

    
    

    // Clear existing markers safely
    projectMarkersRef.current.forEach((marker) => {
      try {
        marker.remove();
        
      } catch (error) {
        
      }
    });
    

    // Create markers for projects
    const newMarkers = [];
    

    // Filter projects based on shared view
    let projectsToShow =
      isSharedView && sharedProjectId
        ? projectsData.filter((project) => project.id === sharedProjectId)
        : projectsData;

    

    // Apply project filters (status and city) only if there are active filters
    const hasActiveFilters =
      (projectFilters &&
        projectFilters.status &&
        projectFilters.status.length > 0) ||
      (projectFilters && projectFilters.city && projectFilters.city !== "");

    

    if (hasActiveFilters) {
      
      projectsToShow = filterProjects(projectsToShow);
      
    }


    
    projectsToShow.forEach((project) => {
      // Skip projects without coordinates
      if (!project.latitude || !project.longitude) {
        console.warn(
          `Project ${project.name} has no coordinates, skipping marker`
        );
        return;
      }

      const coordinates = [
        parseFloat(project.longitude),
        parseFloat(project.latitude),
      ];

      
      const marker = createProjectMarker(project, coordinates);
      if (marker) {
        newMarkers.push(marker);
        

        // Add zoom-based visibility control.
        // IMPORTANT: never write to markerElement.style.transform — Mapbox owns that
        // property for positioning. Overwriting it causes the marker to flash to (0,0)
        // every zoom tick until Mapbox re-applies its translate() on the next frame.
        // Scale/zIndex are applied to the inner logo container instead.
        const handleZoom = () => {
          const zoom = mapRef.current.getZoom();

          const markerElement = marker.getElement();
          const inner = markerElement.firstElementChild;

          // Show project markers at zoom levels below 12
          if (zoom < 12) {
            markerElement.style.display = "flex";
            markerElement.style.zIndex =
              selectedProject === project.id ? "1000" : "100";
            if (inner) {
              inner.style.transform =
                selectedProject === project.id ? "scale(1.2)" : "scale(1)";
            }
          } else {
            markerElement.style.display = "none";
          }
        };

        // Add zoom listener
        mapRef.current.on("zoom", handleZoom);

        // Store the cleanup function
        marker._zoomHandler = handleZoom;

        // Initial visibility check
        handleZoom();
      }
    });

    
    
    projectMarkersRef.current = newMarkers;
    
  };

  // Function to get icon path for landmark type - Updated for animated GIF icons
  const getLandmarkIcon = (type) => {
    const iconMap = {
      mosque: APP_PATHS.MAP_ICONS.MOSQUE,
      school: APP_PATHS.MAP_ICONS.SCHOOL,
      hospital: APP_PATHS.MAP_ICONS.HOSPITAL,
      shopping_center: APP_PATHS.MAP_ICONS.SHOPPING_CENTER,
      mall: APP_PATHS.MAP_ICONS.SHOPPING_CENTER,
      park: APP_PATHS.MAP_ICONS.PARK,
      green_area: APP_PATHS.MAP_ICONS.PARK,
      gas_station: APP_PATHS.MAP_ICONS.GAS_STATION,
      fuel_station: APP_PATHS.MAP_ICONS.GAS_STATION,
      restaurant: APP_PATHS.MAP_ICONS.RESTAURANT,
      bank: APP_PATHS.MAP_ICONS.BANK,
      pharmacy: APP_PATHS.MAP_ICONS.PHARMACY,
      clinic: APP_PATHS.MAP_ICONS.CLINIC,
      police: APP_PATHS.MAP_ICONS.POLICE,
      police_station: APP_PATHS.MAP_ICONS.POLICE,
      fire_station: APP_PATHS.MAP_ICONS.FIRE_STATION,
      hotel: APP_PATHS.MAP_ICONS.HOTEL,
      gym: APP_PATHS.MAP_ICONS.GYM,
      fitness: APP_PATHS.MAP_ICONS.GYM,
      swimming_pool: APP_PATHS.MAP_ICONS.SWIMMING_POOL,
      pool: APP_PATHS.MAP_ICONS.SWIMMING_POOL,
      walking_path: APP_PATHS.MAP_ICONS.WALKING_PATH,
      walkway: APP_PATHS.MAP_ICONS.WALKING_PATH,
      water: APP_PATHS.MAP_ICONS.WATER,
      beach: APP_PATHS.MAP_ICONS.BEACH,
      entrance: APP_PATHS.MAP_ICONS.ENTRANCE,
      gate: APP_PATHS.MAP_ICONS.ENTRANCE,
      culture_center: APP_PATHS.MAP_ICONS.CULTURE_CENTER,
      community_center: APP_PATHS.MAP_ICONS.CULTURE_CENTER,
      parking: APP_PATHS.MAP_ICONS.PARKING,
      other: APP_PATHS.MAP_ICONS.OTHER,
    };
    return iconMap[type] || APP_PATHS.MAP_ICONS.DEFAULT;
  };

  // Function to get Arabic name for landmark type - Updated with new types
  const getLandmarkTypeArabic = (type) => {
    const typeMap = {
      mosque: "مسجد",
      school: "مدرسة",
      hospital: "مستشفى",
      shopping_center: "مركز تسوق",
      mall: "مول",
      park: "حديقة",
      green_area: "منطقة خضراء",
      gas_station: "محطة وقود",
      fuel_station: "محطة وقود",
      restaurant: "مطعم",
      bank: "بنك",
      pharmacy: "صيدلية",
      clinic: "عيادة",
      police: "شرطة",
      police_station: "مركز شرطة",
      fire_station: "مركز إطفاء",
      hotel: "فندق",
      gym: "صالة رياضية",
      fitness: "لياقة بدنية",
      swimming_pool: "مسبح",
      pool: "مسبح",
      walking_path: "ممشى",
      walkway: "ممر مشاة",
      water: "مياه",
      beach: "شاطئ",
      entrance: "مدخل",
      gate: "بوابة",
      culture_center: "مركز ثقافي",
      community_center: "مركز مجتمعي",
      parking: "موقف سيارات",
      other: "أخرى",
    };
    return typeMap[type] || "نقطة اهتمام";
  };

  // Separate function to process landmarks data
  const processLandmarks = async (projectId, landmarksData) => {
    //   `🏛️ Processing ${landmarksData.length} landmarks for project ${projectId}...`
    // );

    // Clean up existing landmark markers
    if (mapRef.current._landmarkMarkers) {
      mapRef.current._landmarkMarkers.forEach((marker) => {
        marker.remove();
      });
      mapRef.current._landmarkMarkers = [];
    }

    // Clean up zoom handler
    if (mapRef.current._landmarkZoomHandler) {
      mapRef.current.off("zoom", mapRef.current._landmarkZoomHandler);
      mapRef.current._landmarkZoomHandler = null;
    }

    // Clean up landmark data
    if (mapRef.current._landmarkData) {
      mapRef.current._landmarkData.forEach((landmarkItem) => {
        if (landmarkItem.marker) {
          landmarkItem.marker.remove();
        }
      });
      mapRef.current._landmarkData = [];
    }

    // Store landmark markers for cleanup
    const landmarkMarkers = [];

    // Create landmark data structure for zoom-based management
    const landmarkData = landmarksData.map((landmark) => ({
      ...landmark,
      marker: null,
      isVisible: false,
    }));

    // Function to create marker element
    const createMarkerElement = (landmark) => {
      // Create a container for the marker
      const markerContainer = document.createElement("div");
      markerContainer.style.display = "flex";
      markerContainer.style.flexDirection = "column";
      markerContainer.style.alignItems = "center";
      markerContainer.style.cursor = "pointer";
      markerContainer.style.zIndex = "1000"; // Ensure landmarks appear above other elements
      markerContainer.className = "landmark-marker";

      // Create icon container - Optimized for animated GIF icons
      const iconContainer = document.createElement("div");
      iconContainer.style.width = "32px";
      iconContainer.style.height = "32px";
      iconContainer.style.display = "flex";
      iconContainer.style.alignItems = "center";
      iconContainer.style.justifyContent = "center";
      iconContainer.style.backgroundColor = "#ffffff";
      iconContainer.style.borderRadius = "50%";
      iconContainer.style.border = "2px solid #007cbf";
      // iconContainer.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
      iconContainer.style.padding = "6px";
      iconContainer.style.transition = "all 0.2s ease";

      // Create icon image element - Optimized for animated GIF
      const iconElement = document.createElement("img");
      iconElement.src = landmark.icon_path
        ? landmark.icon_path.startsWith("http")
          ? landmark.icon_path
          : getStorageUrl(landmark.icon_path)
        : getLandmarkIcon(landmark.type);
      iconElement.style.width = "20px";
      iconElement.style.height = "20px";
      iconElement.style.objectFit = "contain";
      // iconElement.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))';
      iconElement.style.borderRadius = "4px"; // Slight rounding for GIF icons
      iconElement.alt = landmark.name;

      // Add error handling for missing animated GIF images
      iconElement.onerror = () => {
        
        // If the animated GIF fails, try the default type icon
        const defaultIcon = getLandmarkIcon(landmark.type);
        if (iconElement.src !== defaultIcon) {
          iconElement.src = defaultIcon;
        } else {
          // If even the default animated icon fails, use a static fallback
          iconElement.src = "/map_icons/Socity center icon v2.png";
        }
      };

      // Create label element
      const labelElement = document.createElement("div");
      labelElement.textContent = landmark.name;
      labelElement.style.marginTop = "4px";
      labelElement.style.fontSize = "11px";
      labelElement.style.fontWeight = "bold";
      labelElement.style.color = "#004080";
      labelElement.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
      labelElement.style.padding = "2px 6px";
      labelElement.style.borderRadius = "25px";
      labelElement.style.border = "1px solid #007cbf";
      labelElement.style.whiteSpace = "nowrap";
      labelElement.style.boxShadow = "0 1px 4px rgba(0, 0, 0, 0.2)";
      labelElement.style.maxWidth = "120px";
      // labelElement.style.overflow = 'hidden';
      labelElement.style.textOverflow = "ellipsis";
      // Hide label by default
      labelElement.style.display = "none";

      // Track label visibility state
      let isLabelVisible = false;

      // Add hover effects - Enhanced for animated GIF icons
      markerContainer.addEventListener("mouseenter", () => {
        iconContainer.style.transform = "scale(1.15)";
        iconContainer.style.boxShadow = "0 8px 20px rgba(0, 124, 191, 0.4)";
        iconContainer.style.borderColor = "#0099ff";
        // Only change label colors if it's visible
        if (isLabelVisible) {
          labelElement.style.backgroundColor = "#007cbf";
          labelElement.style.color = "#ffffff";
        }
      });

      markerContainer.addEventListener("mouseleave", () => {
        iconContainer.style.transform = "scale(1)";
        iconContainer.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
        iconContainer.style.borderColor = "#007cbf";
        // Only change label colors if it's visible
        if (isLabelVisible) {
          labelElement.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
          labelElement.style.color = "#004080";
        }
      });

      // Add elements to container
      iconContainer.appendChild(iconElement);
      markerContainer.appendChild(iconContainer);
      markerContainer.appendChild(labelElement);

      // Add click event to toggle label visibility
      markerContainer.addEventListener("click", (e) => {
        e.stopPropagation(); // Prevent map click events

        // Toggle label visibility
        isLabelVisible = !isLabelVisible;
        labelElement.style.display = isLabelVisible ? "block" : "none";

        // Optional: Hide other landmark labels when showing this one
        if (isLabelVisible && mapRef.current._landmarkMarkers) {
          mapRef.current._landmarkMarkers.forEach((otherMarker) => {
            const otherMarkerElement = otherMarker.getElement();
            const otherLabelElement =
              otherMarkerElement.querySelector("div:last-child");
            if (otherLabelElement && otherLabelElement !== labelElement) {
              otherLabelElement.style.display = "none";
            }
          });
        }
      });

      return markerContainer;
    };

    // Function to handle zoom changes for landmarks
    const handleLandmarkZoomChange = () => {
      const currentZoom = mapRef.current.getZoom();

      landmarkData.forEach((landmarkItem) => {
        const minZoom = landmarkItem.min_zoom || 14;
        const maxZoom = landmarkItem.max_zoom || 22;
        const shouldBeVisible =
          currentZoom >= minZoom && currentZoom <= maxZoom;

        if (shouldBeVisible && !landmarkItem.isVisible) {
          // Create and add marker
          const lng = parseFloat(landmarkItem.longitude);
          const lat = parseFloat(landmarkItem.latitude);

          if (isNaN(lng) || isNaN(lat)) {
            console.error(
              `❌ Invalid coordinates for landmark ${landmarkItem.name}: [${landmarkItem.longitude}, ${landmarkItem.latitude}]`
            );
            return;
          }

          const markerElement = createMarkerElement(landmarkItem);
          const marker = new mapboxgl.Marker(markerElement)
            .setLngLat([lng, lat])
            .addTo(mapRef.current);

          landmarkItem.marker = marker;
          landmarkItem.isVisible = true;
          landmarkMarkers.push(marker);
        } else if (!shouldBeVisible && landmarkItem.isVisible) {
          // Remove marker
          if (landmarkItem.marker) {
            landmarkItem.marker.remove();
            const index = landmarkMarkers.indexOf(landmarkItem.marker);
            if (index > -1) {
              landmarkMarkers.splice(index, 1);
            }
            landmarkItem.marker = null;
            landmarkItem.isVisible = false;
          }
        }
      });
    };

    // Set initial state based on current zoom
    handleLandmarkZoomChange();

    // Add zoom listener
    mapRef.current.on("zoom", handleLandmarkZoomChange);

    // Store the zoom handler for cleanup
    mapRef.current._landmarkZoomHandler = handleLandmarkZoomChange;
    mapRef.current._landmarkData = landmarkData;

    // Store landmark markers for cleanup
    if (!mapRef.current._landmarkMarkers) {
      mapRef.current._landmarkMarkers = [];
    }
    mapRef.current._landmarkMarkers = landmarkMarkers;

    //   `✅ Processed ${landmarksData.length} landmarks for project ${projectId}`
    // );

    // Log landmarks loading status
    if (landmarksData.length > 0) {
      //   `✅ Loaded ${landmarksData.length} landmarks for project ${projectId}`
      // );
    } else {
      
    }

    // Make landmarks loading function available globally for debugging
    if (typeof window !== "undefined") {
      window.debugLoadLandmarks = (projectId) => {
        //   `🔧 Debug: Manually loading landmarks for project ${projectId}`
        // );
        return drowLandMark(projectId);
      };
    }
  };

  // Legacy function for backward compatibility
  const drowLandMark = async (projectId) => {
    try {
      const response = await getProjectLandmarks(projectId);
      const landmarksData = response.data.data;
      await processLandmarks(projectId, landmarksData);
    } catch (error) {
      console.error(
        `❌ Error loading landmarks for project ${projectId}:`,
        error
      );
    }
  };
  // Function to load and add map layers
  const loadMapLayers = async (projectId) => {
    try {
      setIsLayersLoading(true);
      setLayersLoadingMessage(getTranslation("loadingLayers", language));

      // Load landmarks and map layers in parallel
      const [landmarksResponse, mapLayersResponse] = await Promise.all([
        getProjectLandmarks(projectId),
        getProjectMapLayers(projectId),
      ]);

      // Process landmarks
      const landmarksData = landmarksResponse.data.data;
      await processLandmarks(projectId, landmarksData);

      // Process map layers
      const layersData = mapLayersResponse.data.data;
      setMapLayers(layersData);

      if (mapRef.current && layersData.length > 0) {
        await addMapLayersToMap(layersData);
      }

      //   `✅ Loaded ${landmarksData.length} landmarks and ${layersData.length} map layers for project ${projectId}`
      // );

      // List all currently loaded map layers for debugging
      if (mapRef.current) {
        const mapLayers = mapRef.current.getStyle().layers;
        const rasterLayers = mapLayers.filter(
          (layer) => layer.type === "raster"
        );
        //   `Total raster layers on map: ${rasterLayers.length}`,
        //   rasterLayers.map((layer) => layer.id)
        // );
      }
    } catch (error) {
      console.error("❌ Error loading map layers:", error);
    } finally {
      setIsLayersLoading(false);
      setLayersLoadingMessage("");
    }
  };

  // Function to add map layers to the map
  const addMapLayersToMap = async (layersData) => {
    if (!mapRef.current) {
      console.error("Error addMapLayersToMap empty map ");
      return;
    }

    // Remove existing map layers
    layersData.forEach((layer) => {
      // For image layers
      const layerId = `${layer.name}-layer`;
      if (mapRef.current.getLayer(layerId)) {
        mapRef.current.removeLayer(layerId);
      }

      // For GeoJSON layers - remove all possible layer types
      const geojsonLayerIds = [
        `${layer.name}-fill`,
        `${layer.name}-outline`,
        `${layer.name}-line`,
        `${layer.name}-circle`,
        `${layer.name}-labels`,
      ];

      geojsonLayerIds.forEach((id) => {
        if (mapRef.current.getLayer(id)) {
          mapRef.current.removeLayer(id);
        }
      });

      // Remove the source
      if (mapRef.current.getSource(layer.name)) {
        mapRef.current.removeSource(layer.name);
      }
    });

    // Add new map layers
    for (const layer of layersData) {
      if (layer.type === "image" && layer.is_active) {
        try {
          //   coordinates: layer.coordinates,
          //   coordinatesType: typeof layer.coordinates,
          // });

          let coordinates;
          if (typeof layer.coordinates === "string") {
            // Clean the string and parse it
            const cleanCoordinates = layer.coordinates.trim();
            coordinates = JSON.parse(cleanCoordinates);
          } else if (Array.isArray(layer.coordinates)) {
            coordinates = layer.coordinates;
          } else {
            console.error(
              `Invalid coordinates format for layer ${layer.name}:`,
              layer.coordinates
            );
            return;
          }

          // Validate coordinates format
          if (!Array.isArray(coordinates) || coordinates.length !== 4) {
            console.error(
              `Invalid coordinates array for layer ${layer.name}:`,
              coordinates
            );
            return;
          }

          // Use the file_url provided by the API (which points to compressed image if available)
          // API now returns full URLs with CORS support
          const imageUrl =
            layer.file_url ||
            getMapLayerFileUrl(layer.file_path.split("/").pop());

          mapRef.current.addSource(layer.name, {
            type: "image",
            url: imageUrl,
            coordinates: coordinates,
          });

          // Add image layer at the bottom of all layers
          // Find the first available layer to add image layer before it
          let beforeId = null;

          // Get all layers and find the first non-background layer
          const allLayers = mapRef.current.getStyle().layers;
          for (let i = 0; i < allLayers.length; i++) {
            const layerInfo = allLayers[i];
            // Skip Mapbox's built-in background and base layers
            if (
              !layerInfo.id.startsWith("background") &&
              !layerInfo.id.startsWith("land") &&
              !layerInfo.id.startsWith("water") &&
              !layerInfo.id.startsWith("landuse") &&
              !layerInfo.id.startsWith("landcover") &&
              !layerInfo.id.startsWith("natural") &&
              !layerInfo.id.startsWith("admin") &&
              !layerInfo.id.startsWith("road") &&
              !layerInfo.id.startsWith("building") &&
              !layerInfo.id.startsWith("poi") &&
              !layerInfo.id.startsWith("transit") &&
              !layerInfo.id.startsWith("place") &&
              !layerInfo.id.startsWith("hillshade") &&
              !layerInfo.id.startsWith("satellite")
            ) {
              beforeId = layerInfo.id;
              break;
            }
          }

          //   `Adding image layer ${layer.name} ${
          //     beforeId ? "before " + beforeId : "at bottom"
          //   }`
          // );

          mapRef.current.addLayer(
            {
              id: `${layer.name}-layer`,
              type: "raster",
              source: layer.name,
              paint: {
                "raster-opacity": parseFloat(layer.opacity) || 1.0,
              },
              // minzoom: parseInt(layer.min_zoom) || 0,
              // maxzoom: parseInt(layer.max_zoom) || 22,
            },
            beforeId
          );

          //   opacity: parseFloat(layer.opacity),
          //   minZoom: parseInt(layer.min_zoom),
          //   maxZoom: parseInt(layer.max_zoom),
          //   fileUrl: imageUrl,
          //   addedBefore: beforeId || "top",
          // });
        } catch (error) {
          // console.error(`Error adding map layer ${layer.name}:`, error);
          // console.error(`Layer data:`, layer);
        }
      }
      // GeoJSON layers
      else if (layer.type === "geojson" && layer.is_active) {
        try {

          // Extract filename from file_path
          const filename = layer.file_path.split("/").pop();
          const geojsonUrl = getMapLayerFileUrl(filename);

          // Fetch GeoJSON data
          const response = await fetch(geojsonUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch GeoJSON: ${response.statusText}`);
          }

          const geojsonData = await response.json();

          // Add GeoJSON source with data
          mapRef.current.addSource(layer.name, {
            type: "geojson",
            data: geojsonData,
          });

          // Determine layer type based on geometry or layer configuration
          let layerType = layer.layer_type;

          // Auto-detect layer type from GeoJSON geometry if not specified
          if (
            !layerType &&
            geojsonData.features &&
            geojsonData.features.length > 0
          ) {
            const firstFeature = geojsonData.features[0];
            const geometryType = firstFeature.geometry.type;

            switch (geometryType) {
              case "Point":
              case "MultiPoint":
                layerType = "circle";
                break;
              case "LineString":
              case "MultiLineString":
                layerType = "line";
                break;
              case "Polygon":
              case "MultiPolygon":
                layerType = "fill";
                break;
              default:
                layerType = "circle"; // Default fallback
            }

            //   `Auto-detected layer type: ${layerType} for geometry: ${geometryType}`
            // );
          } else {
            layerType = layerType || "fill"; // Default to fill if no detection possible
          }

          if (layerType === "fill") {
            // Determine layer order for GeoJSON layers
            let beforeId = null;

            // If this is a background layer (low display_order), add it after image layers but before other layers
            if (layer.display_order && layer.display_order < 10) {
              // Find the first non-image layer to add this layer before it
              const allLayers = mapRef.current.getStyle().layers;
              for (let i = 0; i < allLayers.length; i++) {
                const layerInfo = allLayers[i];
                // Skip image layers and Mapbox built-in layers, but add before parcels/blocks
                if (
                  !layerInfo.id.includes("-layer") && // Skip image layers (they end with -layer)
                  !layerInfo.id.startsWith("background") &&
                  !layerInfo.id.startsWith("land") &&
                  !layerInfo.id.startsWith("water") &&
                  !layerInfo.id.startsWith("landuse") &&
                  !layerInfo.id.startsWith("landcover") &&
                  !layerInfo.id.startsWith("natural") &&
                  !layerInfo.id.startsWith("admin") &&
                  !layerInfo.id.startsWith("road") &&
                  !layerInfo.id.startsWith("building") &&
                  !layerInfo.id.startsWith("poi") &&
                  !layerInfo.id.startsWith("transit") &&
                  !layerInfo.id.startsWith("place") &&
                  !layerInfo.id.startsWith("hillshade") &&
                  !layerInfo.id.startsWith("satellite") &&
                  (layerInfo.id === "parcels-fill" ||
                    layerInfo.id === "blocks-fill")
                ) {
                  beforeId = layerInfo.id;
                  break;
                }
              }
            }

            //   `Adding GeoJSON fill layer ${layer.name} ${
            //     beforeId ? "before " + beforeId : "at top"
            //   }, display_order: ${layer.display_order}`
            // );

            // Add fill layer for polygons
            mapRef.current.addLayer(
              {
                id: `${layer.name}-fill`,
                type: "fill",
                source: layer.name,
                paint: {
                  "fill-color": layer.fill_color || "#0074D9",
                  "fill-opacity": parseFloat(layer.opacity) || 0.6,
                },
                minzoom: parseInt(layer.min_zoom) || 0,
                maxzoom: parseInt(layer.max_zoom) || 22,
              },
              beforeId
            );

            // Add outline layer
            mapRef.current.addLayer(
              {
                id: `${layer.name}-outline`,
                type: "line",
                source: layer.name,
                paint: {
                  "line-color": layer.stroke_color || "#0074D9",
                  "line-width": parseInt(layer.stroke_width) || 2,
                  "line-opacity": parseFloat(layer.opacity) || 1.0,
                },
                minzoom: parseInt(layer.min_zoom) || 0,
                maxzoom: parseInt(layer.max_zoom) || 22,
              },
              beforeId
            );
          } else if (layerType === "line") {
            // Determine layer order for line layers
            let beforeId = null;

            // If this is a background layer (low display_order), add it after image layers but before other layers
            if (layer.display_order && layer.display_order < 10) {
              // Find the first non-image layer to add this layer before it
              const allLayers = mapRef.current.getStyle().layers;
              for (let i = 0; i < allLayers.length; i++) {
                const layerInfo = allLayers[i];
                // Skip image layers and Mapbox built-in layers, but add before parcels/blocks
                if (
                  !layerInfo.id.includes("-layer") && // Skip image layers (they end with -layer)
                  !layerInfo.id.startsWith("background") &&
                  !layerInfo.id.startsWith("land") &&
                  !layerInfo.id.startsWith("water") &&
                  !layerInfo.id.startsWith("landuse") &&
                  !layerInfo.id.startsWith("landcover") &&
                  !layerInfo.id.startsWith("natural") &&
                  !layerInfo.id.startsWith("admin") &&
                  !layerInfo.id.startsWith("road") &&
                  !layerInfo.id.startsWith("building") &&
                  !layerInfo.id.startsWith("poi") &&
                  !layerInfo.id.startsWith("transit") &&
                  !layerInfo.id.startsWith("place") &&
                  !layerInfo.id.startsWith("hillshade") &&
                  !layerInfo.id.startsWith("satellite") &&
                  (layerInfo.id === "parcels-fill" ||
                    layerInfo.id === "blocks-fill")
                ) {
                  beforeId = layerInfo.id;
                  break;
                }
              }
            }

            // Add line layer for linestrings
            mapRef.current.addLayer(
              {
                id: `${layer.name}-line`,
                type: "line",
                source: layer.name,
                paint: {
                  "line-color": layer.stroke_color || "#0074D9",
                  "line-width": parseInt(layer.stroke_width) || 2,
                  "line-opacity": parseFloat(layer.opacity) || 1.0,
                },
                minzoom: parseInt(layer.min_zoom) || 0,
                maxzoom: parseInt(layer.max_zoom) || 22,
              },
              beforeId
            );
          } else if (layerType === "circle") {
            // Determine layer order for circle layers
            let beforeId = null;

            // If this is a background layer (low display_order), add it after image layers but before other layers
            if (layer.display_order && layer.display_order < 10) {
              // Find the first non-image layer to add this layer before it
              const allLayers = mapRef.current.getStyle().layers;
              for (let i = 0; i < allLayers.length; i++) {
                const layerInfo = allLayers[i];
                // Skip image layers and Mapbox built-in layers, but add before parcels/blocks
                if (
                  !layerInfo.id.includes("-layer") && // Skip image layers (they end with -layer)
                  !layerInfo.id.startsWith("background") &&
                  !layerInfo.id.startsWith("land") &&
                  !layerInfo.id.startsWith("water") &&
                  !layerInfo.id.startsWith("landuse") &&
                  !layerInfo.id.startsWith("landcover") &&
                  !layerInfo.id.startsWith("natural") &&
                  !layerInfo.id.startsWith("admin") &&
                  !layerInfo.id.startsWith("road") &&
                  !layerInfo.id.startsWith("building") &&
                  !layerInfo.id.startsWith("poi") &&
                  !layerInfo.id.startsWith("transit") &&
                  !layerInfo.id.startsWith("place") &&
                  !layerInfo.id.startsWith("hillshade") &&
                  !layerInfo.id.startsWith("satellite") &&
                  (layerInfo.id === "parcels-fill" ||
                    layerInfo.id === "blocks-fill")
                ) {
                  beforeId = layerInfo.id;
                  break;
                }
              }
            }

            // Add circle layer for points
            mapRef.current.addLayer(
              {
                id: `${layer.name}-circle`,
                type: "circle",
                source: layer.name,
                paint: {
                  "circle-color": layer.fill_color || "#ff6b35",
                  "circle-radius": parseInt(layer.circle_radius) || 3,
                  "circle-opacity": parseFloat(layer.opacity) || 0.8,
                  "circle-stroke-color": layer.stroke_color || "#ffffff",
                  "circle-stroke-width": parseInt(layer.stroke_width) || 1,
                },
                minzoom: parseInt(layer.min_zoom) || 0,
                ...(layer.max_zoom && { maxzoom: parseInt(layer.max_zoom) }),
              },
              beforeId
            );

            // Add labels for points if they have text properties
            if (geojsonData.features && geojsonData.features.length > 0) {
              const firstFeature = geojsonData.features[0];
              const hasTextProperty =
                firstFeature.properties &&
                (firstFeature.properties.length ||
                  firstFeature.properties.label ||
                  firstFeature.properties.name);

              if (hasTextProperty) {
                mapRef.current.addLayer(
                  {
                    id: `${layer.name}-labels`,
                    type: "symbol",
                    source: layer.name,
                    layout: {
                      "text-field":
                        ["get", "length"] || ["get", "label"] || [
                          "get",
                          "name",
                        ] ||
                        "",
                        "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                      "text-size": 8,
                      "text-offset": [0, 2],
                      "text-anchor": "top",
                    },
                    paint: {
                      "text-color": "#000000",
                      "text-halo-color": "#ffffff",
                      "text-halo-width": 1,
                    },

                    minzoom: parseInt(layer.min_zoom) || 0,
                    ...(layer.max_zoom && {
                      maxzoom: parseInt(layer.max_zoom),
                    }),
                  },
                  beforeId
                );
              }
            }
          }

          //   type: layerType,
          //   opacity: parseFloat(layer.opacity),
          //   minZoom: parseInt(layer.min_zoom),
          //   maxZoom: parseInt(layer.max_zoom),
          //   geojsonUrl: geojsonUrl,
          //   displayOrder: layer.display_order,
          //   addedBefore: beforeId || "top",
          // });
        } catch (error) {
          console.error(`Error adding GeoJSON layer ${layer.name}:`, error);
          console.error(`Layer data:`, layer);
        }
      }
    }
  };

  // Function to update map styling based on selection
  const updateMapStyling = () => {
    if (!mapRef.current) return;

    // Check if map has required methods (map might be in process of being destroyed)
    if (typeof mapRef.current.getLayer !== 'function' ||
        typeof mapRef.current.setPaintProperty !== 'function') {
      return;
    }

    try {
      // Update block styling
      if (mapRef.current.getLayer("blocks-fill")) {
        mapRef.current.setPaintProperty("blocks-fill", "fill-color", [
          "case",
          ["==", ["get", "id"], selectedBlockId || -1],
          "#ff6b35", // Orange for selected block
          "#e0e0e0", // Default gray
        ]);

        mapRef.current.setPaintProperty("blocks-outline", "line-color", [
          "case",
          ["==", ["get", "id"], selectedBlockId || -1],
          "#ff4500", // Dark orange for selected block outline
          "#0074D9", // Default blue
        ]);

        mapRef.current.setPaintProperty("blocks-outline", "line-width", [
          "case",
          ["==", ["get", "id"], selectedBlockId || -1],
          5, // Thicker line for selected block
          3, // Default thickness
        ]);
      }

      // Update parcel styling
      if (mapRef.current.getLayer("parcels-fill")) {
        // Keep original colors based on sales status - no background change for selection
        mapRef.current.setPaintProperty("parcels-fill", "fill-color", [
          "case",
          ["==", ["get", "sales_status"], "Available"],
          "#10b981",
          ["==", ["get", "sales_status"], "Not Available"],
          "#3b82f6", // Blue// Emerald green
          ["==", ["get", "sales_status"], "Reserved"],
          "#f59e0b", // Amber
          ["==", ["get", "sales_status"], "Sold"],
          "#ef4444", // Red
          "#6b7280", // Gray
        ]);

        // Only change the outline color and width for selected parcels
        mapRef.current.setPaintProperty("parcels-outline", "line-color", [
          "case",
          ["==", ["get", "id"], selectedParcelId || -1],
          "#FFD700", // Gold/yellow selection border - highly visible
          "#2980b9", // Default blue
        ]);

        mapRef.current.setPaintProperty("parcels-outline", "line-width", [
          "case",
          ["==", ["get", "id"], selectedParcelId || -1],
          5, // Thick gold line for selected parcel
          1, // Default thickness
        ]);

        // Add a pulsing selected layer for extra visibility
        if (!mapRef.current.getLayer("parcels-selected-glow")) {
          mapRef.current.addLayer({
            id: "parcels-selected-glow",
            type: "line",
            source: "parcels",
            paint: {
              "line-color": "#FFD700",
              "line-width": [
                "case",
                ["==", ["get", "id"], selectedParcelId || -1],
                10,
                0,
              ],
              "line-opacity": [
                "case",
                ["==", ["get", "id"], selectedParcelId || -1],
                0.4,
                0,
              ],
              "line-blur": 3,
            },
          }, "parcels-outline");
        } else {
          mapRef.current.setPaintProperty("parcels-selected-glow", "line-width", [
            "case",
            ["==", ["get", "id"], selectedParcelId || -1],
            10,
            0,
          ]);
          mapRef.current.setPaintProperty("parcels-selected-glow", "line-opacity", [
            "case",
            ["==", ["get", "id"], selectedParcelId || -1],
            0.4,
            0,
          ]);
        }
      }
    } catch (error) {
      console.error("Error updating map styling:", error);
      console.error("Selected block ID:", selectedBlockId);
      console.error("Selected parcel ID:", selectedParcelId);
    }
  };

  // Function to zoom to block
  const zoomToBlock = (blockId) => {

    if (!mapRef.current) {
      return;
    }

    const source = mapRef.current.getSource("blocks");

    if (
      source &&
      source._data &&
      source._data.features &&
      source._data.features.length > 0
    ) {
      const block = source._data.features.find(
        (f) => f.properties.id == blockId
      );

      if (block) {
        // Select the block
        setSelectedBlockId(blockId);
        setSelectedParcelId(null); // Clear parcel selection

        try {
          const bounds = new mapboxgl.LngLatBounds();

          // Validate and extend bounds with proper coordinate format
          if (
            block.geometry &&
            block.geometry.coordinates &&
            block.geometry.coordinates[0]
          ) {
            block.geometry.coordinates[0].forEach((coord) => {
              // Ensure coord is a valid array with two numbers
              if (
                Array.isArray(coord) &&
                coord.length >= 2 &&
                typeof coord[0] === "number" &&
                typeof coord[1] === "number" &&
                !isNaN(coord[0]) &&
                !isNaN(coord[1])
              ) {
                bounds.extend(coord);
              } else {
                
              }
            });
          }


          // Check if bounds is valid before fitting
          if (!bounds.isEmpty()) {
            mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 18 });
          } else {
            console.error("Invalid bounds for block:", blockId);
          }
        } catch (error) {
          console.error("Error creating bounds for block:", blockId, error);
        }

        // Update styling after a short delay to ensure the map has updated
        setTimeout(() => updateMapStyling(), 100);
      } else {
        //   "Available block IDs:",
        //   source._data.features.map((f) => f.properties.id)
        // );
      }
    } else {
      if (source) {
      }
    }
  };

  // Function to zoom to parcel
  const zoomToParcel = (parcelId) => {

    if (!mapRef.current) {
      return;
    }

    const source = mapRef.current.getSource("parcels");

    if (
      source &&
      source._data &&
      source._data.features &&
      source._data.features.length > 0
    ) {
      const parcel = source._data.features.find(
        (f) => f.properties.id == parcelId
      );

      if (parcel) {
        // Select the parcel
        setSelectedParcelId(parcelId);
        setSelectedBlockId(null); // Clear block selection

        try {
          const bounds = new mapboxgl.LngLatBounds();

          // Validate and extend bounds with proper coordinate format
          if (
            parcel.geometry &&
            parcel.geometry.coordinates &&
            parcel.geometry.coordinates[0]
          ) {
            parcel.geometry.coordinates[0].forEach((coord) => {
              // Ensure coord is a valid array with two numbers
              if (
                Array.isArray(coord) &&
                coord.length >= 2 &&
                typeof coord[0] === "number" &&
                typeof coord[1] === "number" &&
                !isNaN(coord[0]) &&
                !isNaN(coord[1])
              ) {
                bounds.extend(coord);
              } else {
                
              }
            });
          }


          // Check if bounds is valid before fitting
          if (!bounds.isEmpty()) {
            // Determine max zoom based on project type
            const currentProject = projects.find(
              (p) => p.id === selectedProject
            );
            const maxZoom =
              currentProject && currentProject.project_type === "residential"
                ? 20
                : 18.5;
            mapRef.current.fitBounds(bounds, { padding: 20, maxZoom: maxZoom });
          } else {
            console.error("Invalid bounds for parcel:", parcelId);
          }
        } catch (error) {
          console.error("Error creating bounds for parcel:", parcelId, error);
        }

        // Update styling after a short delay to ensure the map has updated
        setTimeout(() => updateMapStyling(), 100);
      } else {
        //   "Available parcel IDs:",
        //   source._data.features.map((f) => f.properties.id)
        // );
      }
    } else {
      if (source) {
      }
    }
  };

  // Function to recreate base layers after style change
  const recreateBaseLayers = () => {
    if (!mapRef.current) return;


    try {
      // Wait for map to be fully ready
      if (!mapRef.current.isStyleLoaded()) {
        setTimeout(recreateBaseLayers, 100);
        return;
      }

      // Add projects source for future use
      try {
        if (!mapRef.current.getSource("projects")) {
          mapRef.current.addSource("projects", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
        }
      } catch (error) {
        console.error("Error adding projects source:", error);
      }

      // Add blocks source and layers
      try {
        if (!mapRef.current.getSource("blocks")) {
          mapRef.current.addSource("blocks", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [] },
          });
        }

        if (!mapRef.current.getLayer("blocks-fill")) {
          mapRef.current.addLayer({
            id: "blocks-fill",
            type: "fill",
            source: "blocks",
            paint: {
              "fill-color": "#e0e0e0",
              "fill-opacity": 0.5,
            },
            minzoom: 11,
            maxzoom: 14,
          });
        }

        if (!mapRef.current.getLayer("blocks-outline")) {
          mapRef.current.addLayer({
            id: "blocks-outline",
            type: "line",
            source: "blocks",
            paint: {
              "line-color": "#0074D9",
              "line-width": 3,
            },
            minzoom: 11,
            maxzoom: 14,
          });
        }

        if (!mapRef.current.getLayer("blocks-labels")) {
          mapRef.current.addLayer({
            id: "blocks-labels",
            type: "symbol",
            source: "blocks",
            layout: {
              "text-field": ["get", "block_number"],
              "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
              "text-size": 14,
            },
            paint: {
              "text-color": "#004080",
              "text-halo-color": "#ffffff",
              "text-halo-width": 2,
            },
            minzoom: 11,
            maxzoom: 14,
          });
        }
      } catch (error) {
        console.error("Error adding blocks layers:", error);
      }

      // Parcels source and layers will be added only when viewing project details
      //   "✓ Skipping initial parcels source - will be added when viewing project details"
      // );

      // Re-add click events with delay to ensure layers are ready
      setTimeout(() => {
        try {
          setupMapEvents();

          // Re-add zoom change listener
          mapRef.current.off("zoom"); // Remove existing listeners first
          mapRef.current.on("zoom", () => {
            const newZoom = mapRef.current.getZoom();
            setCurrentZoom(newZoom);

            // Handle dynamic style switching
            handleDynamicStyleSwitch(newZoom);

            if (onZoomChange) {
              onZoomChange(newZoom);
            }
          });

        } catch (error) {
          console.error("Error setting up map events:", error);
        }
      }, 100);

    } catch (error) {
      console.error("❌ Error recreating base layers:", error);
    }
  };

  // Function to setup map events
  const setupMapEvents = () => {
    if (!mapRef.current) {
      return;
    }


    try {
      // Remove existing listeners to avoid duplicates
      mapRef.current.off("click", "blocks-fill");
      mapRef.current.off("click", "parcels-fill");
      mapRef.current.off("click");

      // Check if layers exist before adding event listeners
      const hasBlocksFill = mapRef.current.getLayer("blocks-fill");
      const hasParcelsFill = mapRef.current.getLayer("parcels-fill");

      //   hasBlocksFill: !!hasBlocksFill,
      //   hasParcelsFill: !!hasParcelsFill,
      // });

      // Click events for blocks (only if layer exists)
      if (hasBlocksFill) {
        mapRef.current.on("click", "blocks-fill", (e) => {
          const feature = e.features[0];
          const blockId = feature.properties.id;

          // Select the block
          setSelectedBlockId(blockId);
          setSelectedParcelId(null);

          try {
            const bounds = new mapboxgl.LngLatBounds();

            // Validate and extend bounds with proper coordinate format
            if (
              feature.geometry &&
              feature.geometry.coordinates &&
              feature.geometry.coordinates[0]
            ) {
              feature.geometry.coordinates[0].forEach((coord) => {
                // Ensure coord is a valid array with two numbers
                if (
                  Array.isArray(coord) &&
                  coord.length >= 2 &&
                  typeof coord[0] === "number" &&
                  typeof coord[1] === "number" &&
                  !isNaN(coord[0]) &&
                  !isNaN(coord[1])
                ) {
                  bounds.extend(coord);
                } else {
                  
                }
              });
            }

            // Check if bounds is valid before fitting
            if (!bounds.isEmpty()) {
              mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 18 });
            } else {
              console.error("Invalid bounds for block click");
            }
          } catch (error) {
            console.error("Error creating bounds for block click:", error);
          }
        });
      } else {
        //   "⚠ Blocks-fill layer not found, skipping block click events"
        // );
      }

      // Parcel click events will be setup separately when parcels layers are added
      //   "⚠ Parcels click events will be setup when parcels layers are created"
      // );

      // Clear selection when clicking on empty area
      mapRef.current.on("click", (e) => {
        // Any click on the map counts as user interaction (hides recent projects bar)
        if (initialAnimationCompleteRef.current) {
          userInteractionStartedRef.current = true;
          if (onMapInteraction) {
            onMapInteraction();
          }
        }

        // Check if click was on a feature
        const layers = [];
        if (hasBlocksFill) layers.push("blocks-fill");
        if (hasParcelsFill) layers.push("parcels-fill");

        if (layers.length > 0) {
          const features = mapRef.current.queryRenderedFeatures(e.point, {
            layers,
          });

          if (features.length === 0) {
            // Clicked on empty area - clear selections and hide popover
            setSelectedParcelId(null);
            setSelectedBlockId(null);
            setSelectedParcelData(null);
            setIsParcelPopoverVisible(false);
            setIsUnitDetailsVisible(false);

            // Hide parcel dimensions
            hideParcelDimensions();

            // Close popup if exists
            if (popupRef.current) {
              popupRef.current.remove();
              popupRef.current = null;
            }
          }
        }
      });

      // Try to setup parcels click events if parcels layer exists
      // setTimeout(() => {
      //   setupParcelsClickEvents();
      // }, 200);

    } catch (error) {
      console.error("❌ Error setting up map events:", error);
    }
  };

  // Load essential data immediately (projects only) - NO LoadingOverlay
  const loadEssentialData = async () => {
    if (dataLoaded) return;

    try {

      // Wait for map to be ready
      if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
        setTimeout(() => loadEssentialData(), 500);
        return;
      }

      // Use projects from context or load if needed
      let projectsData = projects;
      if (!projectsData || projectsData.length === 0) {
        projectsData = await loadProjects();
      } else {
      }

      if (!projectsData || projectsData.length === 0) {
        
        setDataLoaded(true);
        return;
      }

      // Add project markers to map immediately
      updateProjectMarkers(projectsData);

      // Mark essential data as loaded
      setDataLoaded(true);

      // Start background loading of additional data
      loadBackgroundData(projectsData);
    } catch (error) {
      console.error("Error loading essential data:", error);
      setDataLoaded(true); // Still mark as loaded to prevent infinite retries
    }
  };

  // Load additional data in background without blocking UI
  const loadBackgroundData = async (projectsData) => {
    try {

      // Determine which projects to load data for
      const projectsToLoad = selectedProject
        ? [projectsData.find((p) => p.id === selectedProject)].filter(Boolean)
        : projectsData; // Load all projects if none selected

      //   `Loading background data for ${projectsToLoad.length} project(s)`
      // );

      let allBlocks = [];
      let allParcels = [];

      // Load map layers, landmarks, and blocks in parallel for all projects
      const backgroundPromises = projectsToLoad.map(async (project) => {
        try {
          //   `🔄 Loading background data for project ${project.id}: ${project.name}`
          // );

          // Load map layers and landmarks in parallel
          const [landmarksResponse, mapLayersResponse, blocksResponse] =
            await Promise.all([
              getProjectLandmarks(project.id).catch((error) => {
                console.error(
                  `❌ Error loading landmarks for project ${project.id}:`,
                  error
                );
                return { data: { data: [] } };
              }),
              getProjectMapLayers(project.id).catch((error) => {
                console.error(
                  `❌ Error loading map layers for project ${project.id}:`,
                  error
                );
                return { data: { data: [] } };
              }),
              getBlocks(project.id).catch((error) => {
                console.error(
                  `❌ Error loading blocks for project ${project.id}:`,
                  error
                );
                return { data: { data: [] } };
              }),
            ]);

          // Process landmarks
          const landmarksData = landmarksResponse.data.data;
          if (landmarksData.length > 0) {
            await processLandmarks(project.id, landmarksData);
            //   `✅ Background: Loaded ${landmarksData.length} landmarks for project ${project.id}`
            // );
          }

          // Process map layers
          const layersData = mapLayersResponse.data.data;
          if (layersData.length > 0) {
            setMapLayers((prevLayers) => [...prevLayers, ...layersData]);
            if (mapRef.current) {
              await addMapLayersToMap(layersData);
            }
            //   `✅ Background: Loaded ${layersData.length} map layers for project ${project.id}`
            // );
          }

          // Return blocks data for further processing
          return {
            projectId: project.id,
            blocks: blocksResponse.data.data,
            project: project,
          };
        } catch (error) {
          console.error(
            `❌ Error loading background data for project ${project.id}:`,
            error
          );
          return {
            projectId: project.id,
            blocks: [],
            project: project,
          };
        }
      });

      // Wait for all background data to load
      const backgroundResults = await Promise.all(backgroundPromises);

      // Process blocks
      const allBlocksFlat = backgroundResults.flatMap(
        (result) => result.blocks
      );
      //   `✅ Background: Total blocks loaded: ${allBlocksFlat.length}`
      // );

      if (allBlocksFlat.length > 0) {
        // Add all blocks
        for (const block of allBlocksFlat) {
          allBlocks.push({
            type: "Feature",
            geometry: JSON.parse(block.coordinates),
            properties: {
              id: block.id,
              block_number: block.block_number,
              project_id: block.project_id,
            },
          });
        }
      }

      // Load parcels only when viewing project details (selectedProject exists)
      if (selectedProject && projectsToLoad.length === 1) {

        // Check if we can use direct URLs
        const canUseDirectUrls = spacesService.isDirectAccessAvailable();

        if (canUseDirectUrls) {
          //   "🌐 Direct URL access is available - using optimized loading"
          // );
        } else {
          //   "📡 Direct URL access not available - using traditional API method"
          // );
        }

        if (canUseDirectUrls) {
          // Single project - use direct URL in map source for optimal performance
          const project = projectsToLoad[0];
   const directUrl = spacesService.getDirectUrl(
            project.id,
            "compressed"
          );
          // Skip direct URL loading for residential projects
          if (project.project_type === "residential") {
            //   `🏢 Skipping direct URL loading for residential project ${project.id} - will use floor-based loading`
            // );

            // For residential projects, create empty source and let floor loading handle the data
            if (mapRef.current.getSource("parcels")) {
              // Remove layers first
              const layersToRemove = [
                "parcels-fill",
                "parcels-outline",
                "parcels-labels",
                "parcels-prices",
              ];
              layersToRemove.forEach((layerId) => {
                if (mapRef.current.getLayer(layerId)) {
                  mapRef.current.removeLayer(layerId);
                }
              });
              mapRef.current.removeSource("parcels");
            }


            // Add empty source for residential projects - floor-based loading will populate it
            mapRef.current.addSource("parcels", {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [],
              },
            });

            // Re-add layers
            await addParcelsLayers();
            //   `🏢 Created empty parcels source for residential project ${project.id}`
            // );

            return; // Don't load any static data for residential projects
          }

       

          //   `🌐 Background: Using direct URL for single project ${project.id}: ${directUrl}`
          // );

          try {
            // Remove existing parcels source and recreate with direct URL
            if (mapRef.current.getSource("parcels")) {
              // Remove layers first
              const layersToRemove = [
                "parcels-fill",
                "parcels-outline",
                "parcels-labels",
                "parcels-prices",
              ];
              layersToRemove.forEach((layerId) => {
                if (mapRef.current.getLayer(layerId)) {
                  mapRef.current.removeLayer(layerId);
                }
              });
              mapRef.current.removeSource("parcels");
            }

            // Add source with direct URL test
            mapRef.current.addSource("parcels", {
              type: "geojson",
              data: directUrl,
            });

            //   `✅ Background: Added parcels source with direct URL for project ${project.id}`
            // );

            // Re-add layers
            await addParcelsLayers();

            //   `🎨 Background: Parcels layers re-added for direct URL source`
            // );

            // Load data for statistics calculation only (in background)
            setTimeout(async () => {
              try {
                // Skip loading static data for residential projects
                if (project.project_type === "residential") {
                  //   `🏢 Skipping background data loading for residential project ${project.id}`
                  // );
                  return;
                }

                const response = await getProjectParcelsGeoJsonStatic(
                  project.id
                );
                if (response.data && response.data.features) {
                  const parcelsData = response.data.features;
                  setAllParcelsData(parcelsData);

                  // Calculate price statistics
                  if (onPriceStatsUpdate) {
                    const prices = parcelsData
                      .map(
                        (parcel) =>
                          parseFloat(parcel.properties?.price_with_tax) || 0
                      )
                      .filter((price) => price > 0);

                    if (prices.length > 0) {
                      const minPrice = Math.min(...prices);
                      const maxPrice = Math.max(...prices);
                      const priceStats = { min: minPrice, max: maxPrice };
                      //   "📊 Background: Calculated price statistics from direct URL data:",
                      //   priceStats
                      // );
                      onPriceStatsUpdate(priceStats);
                    }
                  }
                }
              } catch (statsError) {
                console.warn(
                  "⚠️ Could not load data for statistics calculation:",
                  statsError
                );
              }
            }, 100); // Load stats in background after map is updated
          } catch (error) {
            console.error(
              `❌ Background: Error using direct URL for project ${project.id}, falling back to traditional method:`,
              error
            );
            // Fall back to traditional method for this project only
            try {
              const response = await getProjectParcelsGeoJsonStatic(project.id);
              if (response.data && response.data.features) {
                allParcels = response.data.features;
                setAllParcelsData(allParcels);

                // Update parcels on map using setData method
                const parcelsSource = mapRef.current.getSource("parcels");
                if (parcelsSource) {
                  parcelsSource.setData({
                    type: "FeatureCollection",
                    features: allParcels,
                  });
                  //   `✅ Background: Updated map with ${allParcels.length} parcels using fallback method`
                  // );
                }
              }
            } catch (fallbackError) {
              console.error(
                `❌ Background: Fallback method also failed for project ${project.id}:`,
                fallbackError
              );
            }
          }
        } else {
          // Direct URLs not available - use traditional method for selected project
          //   "🔄 Background: Using traditional method (direct URLs not available)"
          // );
          try {
            const project = projectsToLoad[0];
            const response = await getProjectParcelsGeoJsonStatic(project.id);
            if (response.data && response.data.features) {
              allParcels = response.data.features;
              setAllParcelsData(allParcels);

              // Create parcels source if it doesn't exist
              if (!mapRef.current.getSource("parcels")) {
                mapRef.current.addSource("parcels", {
                  type: "geojson",
                  data: { type: "FeatureCollection", features: [] },
                });
                await addParcelsLayers();
              }

              // Update parcels on map using setData method
              const parcelsSource = mapRef.current.getSource("parcels");
              if (parcelsSource) {
                parcelsSource.setData({
                  type: "FeatureCollection",
                  features: allParcels,
                });
                //   `✅ Background: Updated map with ${allParcels.length} parcels using traditional method`
                // );
              }
            }
          } catch (error) {
            console.error(`❌ Background: Traditional method failed:`, error);
          }
        }
      } else {
        //   "🔄 Background: No selected project - skipping parcels loading"
        // );
      }

      // Update map with all background data
      if (mapRef.current && mapRef.current.isStyleLoaded()) {

        // Update blocks
        const blocksSource = mapRef.current.getSource("blocks");
        if (blocksSource && allBlocks.length > 0) {
          blocksSource.setData({
            type: "FeatureCollection",
            features: allBlocks,
          });
          //   `✅ Background: Updated map with ${allBlocks.length} blocks`
          // );
        }

        // Parcels are handled directly in the loading logic above
      }

      // Apply any existing filters after data is loaded
      setTimeout(() => {
        const currentFilters = {
          salesStatus: statusFilters,
          priceRange: priceFilter,
          services: usageFilters,
          floors: [],
        };
        //   "Background: Applying filters after data load:",
        //   currentFilters
        // );
        applyFiltersToMap(currentFilters);
      }, 500);

      // Notify parent component that map is ready
      if (onMapReady) {
        //   zoomToBlock,
        //   zoomToParcel,
        // });
        onMapReady({ zoomToBlock, zoomToParcel });
      }

      //   projects: projectsData.length,
      //   blocks: allBlocks.length,
      //   parcels: allParcels.length,
      // });
    } catch (error) {
      console.error("❌ Error loading background data:", error);
    }
  };

  // Function to add parcels source and layers when viewing project details using new service
  const addParcelsSourceAndLayers = async (projectId) => {
    MapLogger.logOperationStart('add-parcels-source-and-layers', { projectId });

    if (!mapRef.current || !projectId) {
      MapLogger.logWarning('add-parcels-source-and-layers', 'Map or project ID not available');
      return;
    }

    try {
      // Find the project
      const project = projects.find((p) => p.id === projectId);
      if (!project) {
        MapLogger.logError('add-parcels-source-and-layers', 'Project not found', new Error('Project not found'), { projectId });
        return;
      }

      // Use new service based on project type
      let result;
      if (project.project_type === "residential") {
        // For residential projects, we need floor filters
        // This will be handled by the floor filter system
        MapLogger.logMapInfo('Residential project - will be handled by floor filter system', { projectId });
        return;
      } else {
        // For land projects, use the land project service
        // Add a small delay to ensure controller is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        result = await displayLandProject(projectId, project);
      }

      if (result && result.success) {
        MapLogger.logOperationEnd('add-parcels-source-and-layers', true, { projectId });
      } else {
        MapLogger.logOperationEnd('add-parcels-source-and-layers', false, { 
          projectId, 
          error: result?.error || 'Unknown error' 
        });
      }
    } catch (error) {
      MapErrorHandler.handleProjectLoadError(error, projectId, { 
        operation: 'add-parcels-source-and-layers' 
      });
      MapLogger.logOperationEnd('add-parcels-source-and-layers', false, { 
        projectId, 
        error: error.message 
      });
    }
  };

  // Helper function to add parcels layers to the map
  const addParcelsLayers = async () => {
    if (!mapRef.current) return;

    try {
      // Add parcels fill layer
      if (!mapRef.current.getLayer("parcels-fill")) {
        mapRef.current.addLayer({
          id: "parcels-fill",
          type: "fill",
          source: "parcels",
          paint: {
            "fill-color": [
              "case",
              ["==", ["get", "sales_status"], "Available"],
              "#10b981", // Emerald green
              ["==", ["get", "sales_status"], "Not Available"],
              "#3b82f6", // Blue
              ["==", ["get", "sales_status"], "Reserved"],
              "#f59e0b", // Amber
              ["==", ["get", "sales_status"], "Sold"],
              "#ef4444", // Red
              "#6b7280", // Gray
            ],
            "fill-opacity": 0.5,
          },
          minzoom: 14,
        });
      }

      // Add parcels outline layer
      if (!mapRef.current.getLayer("parcels-outline")) {
        mapRef.current.addLayer({
          id: "parcels-outline",
          type: "line",
          source: "parcels",
          paint: {
            "line-color": "#2980b9",
            "line-width": 1,
          },
          minzoom: 14,
        });
      }

      // Add parcels labels layer
      if (!mapRef.current.getLayer("parcels-labels")) {
        mapRef.current.addLayer({
          id: "parcels-labels",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": ["get", "parcel_number"],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],

            "text-size": 12,
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 16,
        });
      }

      // Add price display layer (initially hidden)
      if (!mapRef.current.getLayer("parcels-prices")) {
        mapRef.current.addLayer({
          id: "parcels-prices",
          type: "symbol",
          source: "parcels",
          layout: {
            "text-field": [
              "format",
              ["get", "parcel_number"],
              { "font-scale": 1.4, "text-color": "#ffffff" },
              
              "\n",
              {},
              ["to-string", ["get", "price_with_tax"]],
              { "font-scale": 1.0, "text-color": "#626262" },
              " SAR",
              { "font-scale": 1.0, "text-color": "#626262" },
            ],
            "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
            "text-size": 12,
            // "text-weight": "bold",

            "text-offset": [0, 0],
            "text-anchor": "center",
            "text-allow-overlap": false,
            "text-ignore-placement": false,
            visibility: "none", // Initially hidden
          },
          paint: {
            "text-color": "#ffffff",
            "text-halo-color": "#000000",
            "text-halo-width": 0,
          },
          minzoom: 14,
        });
      }

      // Add click events for parcels after layers are created
      // setTimeout(() => {
      //   setupParcelsClickEvents();
      // }, 100);
    } catch (error) {
      console.error("Error adding parcels layers:", error);
    }
  };

  // Function to setup click events specifically for parcels
  const setupParcelsClickEvents = () => {

    if (!mapRef.current) {
      return;
    }

    try {
      // Remove existing parcel click listeners to avoid duplicates
      mapRef.current.off("click", "parcels-fill");

      // Check if parcels layer exists
      const hasParcelsFill = mapRef.current.getLayer("parcels-fill");

      if (hasParcelsFill) {
        // mapRef.current.on("click", "parcels-fill", async (e) => {

        //   // Debounce clicks to prevent rapid multiple calls
        //   const now = Date.now();
        //   if (now - lastClickTimeRef.current < 300) {
        //     // 300ms debounce
        //     return;
        //   }
        //   lastClickTimeRef.current = now;

        //   const feature = e.features[0];
        //   const parcelId = feature.properties.id;
        //   const parcelNumber = feature.properties.parcel_number;


        //   // Select the parcel
        //   setSelectedParcelId(parcelId);
        //   setSelectedBlockId(null);

        //   // Show loading state
        //   //   language === "ar"
        //   //     ? `جاري تحميل تفاصيل الوحدة ${parcelNumber}...`
        //   //     : `Loading details for unit ${parcelNumber}...`
        //   // );

        //   // Close existing popup if any
        //   if (popupRef.current) {
        //     popupRef.current.remove();
        //     popupRef.current = null;
        //   }

        //   // Hide any existing dimensions before starting new selection
        //   hideParcelDimensions();

        //   // Simple and direct approach: Zoom to parcel and show dimensions
        //   const parcelGeometry = feature.geometry;
        //   //   "🎯 Starting zoom and dimensions for parcel:",
        //   //   parcelNumber
        //   // );

        //   // Calculate center and bottom position of parcel
        //   let parcelCenter = e.lngLat;
        //   let popupPosition = e.lngLat;

        //   if (
        //     parcelGeometry &&
        //     parcelGeometry.coordinates &&
        //     parcelGeometry.coordinates[0]
        //   ) {
        //     try {
        //       // Calculate center and find bottom edge from coordinates
        //       const coords = parcelGeometry.coordinates[0];
        //       let totalLng = 0,
        //         totalLat = 0,
        //         count = 0;
        //       let minLat = Infinity; // For finding the bottom edge

        //       coords.forEach((coord) => {
        //         if (
        //           Array.isArray(coord) &&
        //           coord.length >= 2 &&
        //           typeof coord[0] === "number" &&
        //           typeof coord[1] === "number"
        //         ) {
        //           totalLng += coord[0];
        //           totalLat += coord[1];
        //           count++;

        //           // Track minimum latitude (southernmost point)
        //           if (coord[1] < minLat) {
        //             minLat = coord[1];
        //           }
        //         }
        //       });

        //       if (count > 0) {
        //         parcelCenter = [totalLng / count, totalLat / count];

        //         // Position popup slightly below the bottom edge of parcel
        //         // Add small offset to ensure popup doesn't overlap with parcel
        //         const latOffset = 0.0; // Small offset to move popup below parcel
        //         popupPosition = [totalLng / count, minLat - latOffset];

        //         //   "📍 Calculated popup position (below bottom edge):",
        //         //   popupPosition
        //         // );
        //         //   "📐 Bottom edge latitude:",
        //         //   minLat,
        //         //   "Popup latitude:",
        //         //   minLat - latOffset
        //         // );
        //       }
        //     } catch (error) {
        //       console.error("Error calculating parcel positions:", error);
        //     }
        //   }

        //   // Determine zoom level based on project type
        //   const currentProject = projects.find((p) => p.id === selectedProject);
        //   const zoomLevel =
        //     currentProject && currentProject.project_type === "residential"
        //       ? 20
        //       : 18;
        //   //   `🔍 Zooming to parcel at level ${zoomLevel} (project type: ${currentProject?.project_type})`
        //   // );

        //   // Wait for zoom to complete, then show popup and dimensions
        //   mapRef.current.once("moveend", async () => {

        //     // Create parcel data with geometry for dimensions
        //     const parcelDataWithCoords = {
        //       id: parcelId,
        //       parcel_number: parcelNumber,
        //       ...feature.properties,
        //       coordinates: parcelGeometry,
        //     };

        //     // Store parcel data
        //     setSelectedParcelData(parcelDataWithCoords);

        //     // Show dimensions
        //     if (parcelGeometry) {
        //       showParcelDimensions(parcelDataWithCoords);
        //     }

        //     // Show modal for parcel
        //     await createParcelModal(parcelDataWithCoords, popupPosition);
        //   });

        //   // Zoom with slight offset to accommodate popup below parcel
        //   const mapContainer = mapRef.current.getContainer();
        //   const mapHeight = mapContainer.offsetHeight;
        //   const offsetY = mapHeight * 0.1; // 15% offset upward to make room for popup

        //   mapRef.current.flyTo({
        //     center: parcelCenter,
        //     zoom: zoomLevel,
        //     duration: 1000,
        //     offset: [0, -offsetY], // Negative Y moves map up, making room for popup below
        //   });

        //   // Load detailed data in background
        //   setTimeout(async () => {
        //     try {
        //       const response = await getParcelWithProject(parcelId);
        //       const detailedParcelData = response.data.data;


        //       // Update stored data with detailed information (preserve geometry)
        //       const updatedParcelData = {
        //         ...detailedParcelData,
        //         coordinates: parcelGeometry, // Keep the original geometry for dimensions
        //       };

        //       setSelectedParcelData(updatedParcelData);
        //     } catch (error) {
        //       console.error("Error fetching detailed parcel data:", error);
        //     }
        //   }, 100);
        // });

      } else {
        //   "⚠️ Parcels-fill layer not found, skipping parcel click events"
        // );
      }
    } catch (error) {
      console.error("❌ Error setting up parcels click events:", error);
    }
  };

  // Helper function to load parcels using traditional method (for fallback)
  const loadParcelsTraditionalMethod = async (projectsToLoad) => {

    const parcelPromises = projectsToLoad.map(async (project) => {
      try {
        // Skip loading static GeoJSON for residential projects - they will load floor data separately
        if (project.project_type === "residential") {
          //   `🏢 Skipping static GeoJSON for residential project ${project.id} - will load floor data separately`
          // );
          return [];
        }

        //   `🚀 Loading parcels for project ${project.id} from static GeoJSON...`
        // );
        const response = await getProjectParcelsGeoJsonStatic(project.id);

        // Log the source of the data
        const source = response.headers?.["x-source"] || "api";
        const cached = response.headers?.["x-cached"] || "MISS";
        //   `📊 Project ${project.id} data source: ${source} (cache: ${cached})`
        // );

        // GeoJSON response already has the correct format
        if (response.data && response.data.features) {
          const parcelsCount = response.data.features.length;
          if (parcelsCount === 0) {
            //   `ℹ️ Project ${project.id} has no parcels (empty project)`
            // );
          } else {
            //   `✅ Loaded ${parcelsCount} parcels for project ${project.id} from ${source}`
            // );
          }
          return response.data.features;
        }

        return [];
      } catch (error) {
        console.error(
          `❌ Error loading parcels for project ${project.id}:`,
          error
        );
        return [];
      }
    });

    const allParcelArrays = await Promise.all(parcelPromises);
    const allParcels = allParcelArrays.flat();


    if (allParcels.length > 0) {
      // Store all parcels data for filtering
      setAllParcelsData(allParcels);

      // Calculate and update price statistics
      if (onPriceStatsUpdate) {
        const prices = allParcels
          .map((parcel) => parseFloat(parcel.properties?.price_with_tax) || 0)
          .filter((price) => price > 0);

        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          const priceStats = { min: minPrice, max: maxPrice };
          onPriceStatsUpdate(priceStats);
        }
      }

      // Update parcels on map
      const parcelsSource = mapRef.current.getSource("parcels");
      if (parcelsSource) {
        parcelsSource.setData({
          type: "FeatureCollection",
          features: allParcels,
        });
      }
    }

    return allParcels;
  };

  // Function to validate and restore map data after visibility change
  const validateAndRestoreMapData = async () => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) {
      return;
    }


    try {
      // Check if project markers exist
      const hasProjectMarkers = projectMarkersRef.current.length > 0;

      // Check if projects source has data
      let hasProjectsData = false;
      try {
        const projectsSource = mapRef.current.getSource("projects");
        if (projectsSource && projectsSource._data && projectsSource._data.features) {
          hasProjectsData = projectsSource._data.features.length > 0;
        }
      } catch (error) {
        
      }

      // Check if parcels layers exist (if a project is selected)
      let hasParcelsData = true; // Assume true if no project selected
      if (selectedProject) {
        try {
          const hasParcelsFill = mapRef.current.getLayer("parcels-fill");
          const parcelsSource = mapRef.current.getSource("parcels");
          hasParcelsData = hasParcelsFill && parcelsSource &&
                          parcelsSource._data &&
                          parcelsSource._data.features &&
                          parcelsSource._data.features.length > 0;
        } catch (error) {
          
          hasParcelsData = false;
        }
      }

      //   hasProjectMarkers,
      //   hasProjectsData,
      //   hasParcelsData,
      //   selectedProject: selectedProject,
      //   selectedProjectExists: !!selectedProject,
      //   projectsCount: projects.length,
      //   projectsFromContext: projects.length > 0 ? projects.map(p => p.id) : []
      // });

      // Restore missing data
      if (!hasProjectMarkers || !hasProjectsData) {
        if (projects.length > 0) {
          updateProjectMarkers(projects);

          // Update projects source
          if (mapRef.current.getSource("projects")) {
            const projectFeatures = projects.map((project) => ({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [project.longitude, project.latitude],
              },
              properties: {
                id: project.id,
                name: language === "ar" ? project.name_ar : project.name_en,
                ...project,
              },
            }));

            mapRef.current.getSource("projects").setData({
              type: "FeatureCollection",
              features: projectFeatures,
            });
          }
        } else {
          // Try to reload projects if they're missing
          if (loadProjects) {
            try {
              await loadProjects(true); // Force reload
            } catch (error) {
              console.error("Error reloading projects:", error);
            }
          }
        }
      }

      // Note: Removed automatic project restoration from localStorage
      // to prevent auto-navigation to last viewed project on page load

      // Restore parcels data if needed and project is selected
      if (selectedProject && !hasParcelsData) {
        // Force reload parcels for the selected project
        setDataLoaded(false);
        await loadEssentialData();
        addParcelsSourceAndLayers(selectedProject);
      }


    } catch (error) {
      console.error("Error during map data validation:", error);
    }
  };

  // Function to save map state to localStorage
  const saveMapState = () => {
    try {
      const mapState = {
        selectedProject,
        selectedBlockId,
        selectedParcelId,
        timestamp: Date.now()
      };
      localStorage.setItem('realmap_mapState', JSON.stringify(mapState));
    } catch (error) {
      
    }
  };

  // Function to restore map state from localStorage
  const restoreMapState = () => {
    try {
      const saved = localStorage.getItem('realmap_mapState');
      if (saved) {
        const mapState = JSON.parse(saved);
        // Only restore if saved within last 30 minutes
        if (Date.now() - mapState.timestamp < 30 * 60 * 1000) {
          return mapState;
        } else {
          localStorage.removeItem('realmap_mapState');
        }
      }
    } catch (error) {
      
    }
    return null;
  };

  // Legacy function for compatibility - now calls the new approach
  const loadAllData = async () => {
    await loadEssentialData();
  };

  useEffect(() => {



    // Wait for map configuration to load
    if (configLoading || !mapConfig) {
      return;
    }

    // Determine the style URL based on the mapStyle
    let styleUrl;
    if (mapStyle === "satellite-v9") {
      styleUrl = `${window.location.origin}/styles/satellite-custom.json`;
    } else {
      styleUrl = `mapbox://styles/mapbox/${mapStyle}`;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: styleUrl,
      center: mapConfig?.center || MAPBOX_CONFIG.DEFAULT_CENTER, // مركز السعودية
      zoom: mapConfig?.zoom || MAPBOX_CONFIG.DEFAULT_ZOOM,
      // Mobile optimizations
      touchZoomRotate: true,
      touchPitch: false,
      dragRotate: true, // Enable finger rotation
      pitchWithRotate: false,
      dragPan: true,
      keyboard: true,
      doubleClickZoom: true,
      scrollZoom: true,
    });

    mapRef.current = map;

    // Make map instance globally available for controls
    window.mapInstance = map;

    map.on("load", () => {

      // delay 1 sec
      setTimeout(() => {
         if (mapRef?.current) {
              mapRef.current.flyTo({
                center: [43.81,29.27], // Saudi Arabia center
                zoom: 5,
              });
              
              // Mark initial animation as complete after flyTo finishes
              mapRef.current.once('moveend', () => {
                initialAnimationCompleteRef.current = true;
              });
            }
            }, 1000);




      // Add zoom change listener
      map.on("zoom", () => {
        const newZoom = map.getZoom();
        setCurrentZoom(newZoom);

        // Handle dynamic style switching
        handleDynamicStyleSwitch(newZoom);

        if (onZoomChange) {
          onZoomChange(newZoom);
        }
      });

      // Add map interaction listeners (drag, pan, zoom)
      const handleMapMove = (e) => {
        // Only trigger if initial animation is complete AND user has started interacting
        if (initialAnimationCompleteRef.current && userInteractionStartedRef.current) {
          if (onMapInteraction) {
            onMapInteraction();
          }
        }
      };
      
      // Detect when user starts interacting with the map
      const handleUserInteraction = () => {
        if (initialAnimationCompleteRef.current) {
          userInteractionStartedRef.current = true;
        }
      };
      
      // Store handlers in ref for cleanup
      mapInteractionHandlerRef.current = handleMapMove;
      userInteractionHandlerRef.current = handleUserInteraction;

      // Listen for actual user interactions (not programmatic)
      map.on("mousedown", handleUserInteraction);
      map.on("touchstart", handleUserInteraction);
      map.on("wheel", handleUserInteraction);
      
      map.on("movestart", handleMapMove);
      map.on("drag", handleMapMove);
      map.on("zoomstart", handleMapMove);

      // Project markers will be created dynamically when data loads

      // Add projects source for future use
      map.addSource("projects", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // Add blocks source
      map.addSource("blocks", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      map.addLayer({
        id: "blocks-fill",
        type: "fill",
        source: "blocks",
        paint: {
          "fill-color": "#e0e0e0",
          "fill-opacity": 0.5,
        },
        minzoom: 11,
        maxzoom: 14,
      });

      map.addLayer({
        id: "blocks-outline",
        type: "line",
        source: "blocks",
        paint: {
          "line-color": "#0074D9",
          "line-width": 3,
        },
        minzoom: 11,
        maxzoom: 14,
      });

      map.addLayer({
        id: "blocks-labels",
        type: "symbol",
        source: "blocks",
        layout: {
          "text-field": ["get", "block_number"],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],

          "text-size": 14,
        },
        paint: {
          "text-color": "#004080",
          "text-halo-color": "#ffffff",
          "text-halo-width": 2,
        },
        minzoom: 11,
        maxzoom: 14,
      });

      // // Parcels source and layers will be added only when viewing project details
      //   "✓ Skipping parcels source in map initialization - will be added when viewing project details"
      // );

      // Setup map events
      setupMapEvents();

      // Load essential data immediately when map loads with a small delay
      setTimeout(() => {
        loadEssentialData();
      }, 100);
    });

    return () => {

      try {
        // Clean up map interaction listeners
        if (map && mapInteractionHandlerRef.current) {
          map.off("movestart", mapInteractionHandlerRef.current);
          map.off("drag", mapInteractionHandlerRef.current);
          map.off("zoomstart", mapInteractionHandlerRef.current);
        }
        
        // Clean up user interaction listeners
        if (map && userInteractionHandlerRef.current) {
          map.off("mousedown", userInteractionHandlerRef.current);
          map.off("touchstart", userInteractionHandlerRef.current);
          map.off("wheel", userInteractionHandlerRef.current);
        }

        // Clean up popups first
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        // Clear active parcel reference
        activeParcelRef.current = null;

        // Clean up project markers
        projectMarkersRef.current.forEach((marker) => {
          try {
            if (marker._zoomHandler) {
              map.off("zoom", marker._zoomHandler);
            }
            marker.remove();
          } catch (error) {
            
          }
        });

        // Clean up landmark markers
        if (map && map._landmarkMarkers) {
          map._landmarkMarkers.forEach((marker) => {
            try {
              marker.remove();
            } catch (error) {
              
            }
          });
        }

        // Clean up landmark zoom handler
        if (map && map._landmarkZoomHandler) {
          try {
            map.off("zoom", map._landmarkZoomHandler);
          } catch (error) {
            
          }
        }

        // Clean up landmark data
        if (map && map._landmarkData) {
          map._landmarkData.forEach((landmarkItem) => {
            try {
              if (landmarkItem.marker) {
                landmarkItem.marker.remove();
              }
            } catch (error) {
              
            }
          });
        }

        // Clean up main zoom listener and remove map
        if (map && typeof map.remove === 'function') {
          try {
            // Remove all event listeners
            map.off("zoom");
            map.off("click");
            map.off("load");

            // Hide parcel dimensions before removing map
            if (typeof map.getLayer === 'function') {
              hideParcelDimensions();
            }

            // Remove the map instance
            map.remove();

            // Clear global reference
            if (window.mapInstance === map) {
              window.mapInstance = null;
            }
          } catch (error) {
            
          }
        }

        // Clear map reference
        mapRef.current = null;

      } catch (error) {
        console.error("Error during OptimizedMap cleanup:", error);
      }
    };
  }, [mapConfig, configLoading]);

  // Load data when selectedProject changes (but not on initial load)
  useEffect(() => {
    if (selectedProject && mapRef.current) {
      // Only reload if we have a different project
      setDataLoaded(false);
      loadEssentialData();

      // Note: addParcelsSourceAndLayers is now called only from the external project selection useEffect
      // This prevents duplicate calls
    }
  }, [selectedProject]);

  // Handle external project selection (from Header, etc.) - simulate project marker click
  useEffect(() => {
    // Skip if project was clicked directly on the map
    if (projectClickedDirectlyRef.current) {
      projectClickedDirectlyRef.current = false; // Reset the flag
      return;
    }

    if (
      selectedProject &&
      projects.length > 0 &&
      mapRef.current &&
      dataLoaded
    ) {
      const project = projects.find((p) => p.id === selectedProject);
      if (project && project.latitude && project.longitude) {
        //   "🎯 External project selection detected, simulating project click:",
        //   project.name
        // );

        // Load landmarks for the selected project
        drowLandMark(selectedProject).catch((error) => {
          console.error("❌ Error loading landmarks:", error);
        });

        const coordinates = [
          parseFloat(project.longitude),
          parseFloat(project.latitude),
        ];

        // Simulate the same behavior as clicking on project marker
        const simulateProjectClick = async () => {
          try {
            // Fly to project location first with offset
            // Use zoom level from database or default based on project type
            const zoomLevel = project.map_zoom_level !== null && project.map_zoom_level !== undefined 
              ? project.map_zoom_level 
              : (project.project_type === "residential" ? 18 : 14);
            const flyToOptions = {
              center: coordinates,
              zoom: zoomLevel,
              essential: false,
            };

            // // Add 20% upward offset to the zoom
            // const currentCenter = mapRef.current.getCenter();
            // const offsetLat = (coordinates[1] - currentCenter.lat) * 0.03;
            // flyToOptions.center = [coordinates[0], coordinates[1] - offsetLat];

            // Start flying to location
            mapRef.current.flyTo(flyToOptions);

            // Wait for the fly animation to complete before showing project details
            mapRef.current.once("moveend", async () => {
              // Skip opening the project card if we're navigating to a specific unit
              const skipProjectCard = Boolean(pendingUnitSelectRef.current) || isSearchingUnitRef.current;
              try {
                // Add parcels source and layers for the selected project
                addParcelsSourceAndLayers(project.id);

                // Use pre-loaded project data if statistics are available, otherwise fetch
                if (
                  project.statistics &&
                  Object.keys(project.statistics).length > 0
                ) {
                  setSelectedProjectData(project);
                  if (!skipProjectCard) {
                    if (onProjectSelect) {
                      onProjectSelect(project);
                    } else {
                      setIsProjectPanelVisible(true);
                    }
                  }
                } else {
                  const response = await getProject(project.id);
                  const projectData = response.data.data;

                  setSelectedProjectData(projectData);
                  if (!skipProjectCard) {
                    if (onProjectSelect) {
                      onProjectSelect(projectData);
                    } else {
                      setIsProjectPanelVisible(true);
                    }
                  }
                }
              } catch (error) {
                console.error("Error fetching project details:", error);
                setSelectedProjectData(project);
                if (!skipProjectCard) {
                  if (onProjectSelect) {
                    onProjectSelect(project);
                  } else {
                    setIsProjectPanelVisible(true);
                  }
                }
              }
            });
          } catch (error) {
            console.error("Error handling external project selection:", error);
            setSelectedProjectData(project);
            const skipProjectCard = Boolean(pendingUnitSelectRef.current) || isSearchingUnitRef.current;
            if (!skipProjectCard) {
              if (onProjectSelect) {
                onProjectSelect(project);
              } else {
                setIsProjectPanelVisible(true);
              }
            }
            // Use zoom level from database or default based on project type
            const zoomLevel = project.map_zoom_level !== null && project.map_zoom_level !== undefined
              ? project.map_zoom_level
              : (project.project_type === "residential" ? 18 : 14);
            mapRef.current.flyTo({ center: coordinates, zoom: zoomLevel });
          }
        };
        //------------------------------

        // Execute the simulation
        simulateProjectClick();
      }
    }
  }, [selectedProject, projects, dataLoaded]);

  // Update markers when projects change or filters change
  useEffect(() => {
    
    
    if (projects.length > 0 && mapRef.current) {
      
      updateProjectMarkers(projects);

      // Also update the projects source on the map to reflect filtered projects
      if (mapRef.current.getSource("projects")) {
        const hasActiveFilters =
          (projectFilters &&
            projectFilters.status &&
            projectFilters.status.length > 0) ||
          (projectFilters && projectFilters.city && projectFilters.city !== "");

        const filteredProjects = hasActiveFilters
          ? filterProjects(projects)
          : projects;

        const projectFeatures = filteredProjects
          .filter((project) => project.latitude && project.longitude)
          .map((project) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [
                parseFloat(project.longitude),
                parseFloat(project.latitude),
              ],
            },
            properties: {
              id: project.id,
              name:
                language === "ar"
                  ? project.name_ar || project.name
                  : project.name_en || project.name,
              description: project.description,
              location: project.location,
              isSelected: selectedProject === project.id,
            },
          }));

        //   "🗺️ Updating projects source with filtered features:",
        //   projectFeatures.length
        // );
        mapRef.current.getSource("projects").setData({
          type: "FeatureCollection",
          features: projectFeatures,
        });
      }
    }
  }, [
    projects,
    selectedProject,
    language,
    projectFilters.status.join(','),
    projectFilters.city,
  ]);

  // Refresh map data when language changes
  useEffect(() => {
    if (dataLoaded && mapRef.current && mapRef.current.isStyleLoaded()) {
      //   "🌐 Language changed, refreshing map data for language:",
      //   language
      // );

      // Update project features with new language-specific names
      if (projects.length > 0) {
        // Filter projects based on current filters only if there are active filters
        const hasActiveFilters =
          (projectFilters &&
            projectFilters.status &&
            projectFilters.status.length > 0) ||
          (projectFilters && projectFilters.city && projectFilters.city !== "");

        const filteredProjects = hasActiveFilters
          ? filterProjects(projects)
          : projects;

        const projectFeatures = filteredProjects
          .filter((project) => project.latitude && project.longitude)
          .map((project) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [
                parseFloat(project.longitude),
                parseFloat(project.latitude),
              ],
            },
            properties: {
              id: project.id,
              name:
                language === "ar"
                  ? project.name_ar || project.name
                  : project.name_en || project.name,
              description: project.description,
              location: project.location,
              isSelected: selectedProject === project.id,
            },
          }));

        // Update projects source if it exists
        const projectsSource = mapRef.current.getSource("projects");
        if (projectsSource) {
          projectsSource.setData({
            type: "FeatureCollection",
            features: projectFeatures,
          });
        }
      }

      // Update project markers with new language-specific names
      updateProjectMarkers(projects);
    }
  }, [language, dataLoaded, projects, selectedProject]);

  // Handle shared project link - simulate project marker click
  useEffect(() => {
    if (
      isSharedView &&
      sharedProjectId &&
      projects.length > 0 &&
      mapRef.current &&
      dataLoaded &&
      !sharedProjectProcessed
    ) {
      setSharedProjectProcessed(true);

      const sharedProject = projects.find((p) => p.id === sharedProjectId);
      if (sharedProject && sharedProject.latitude && sharedProject.longitude) {
        const coordinates = [
          parseFloat(sharedProject.longitude),
          parseFloat(sharedProject.latitude),
        ];

        //   "🎯 Simulating project marker click for shared project:",
        //   sharedProject.name
        // );

        // Simulate the same behavior as clicking on project marker
        const simulateProjectClick = async () => {
          try {
            // Fly to project location first with offset
            // Use zoom level from database or default based on project type
            const zoomLevel = sharedProject.map_zoom_level !== null && sharedProject.map_zoom_level !== undefined 
              ? sharedProject.map_zoom_level 
              : (sharedProject.project_type === "residential" ? 18 : 14);
            const flyToOptions = {
              center: coordinates,
              zoom: zoomLevel,
              essential: false,
            };

            // // Add 20% upward offset to the zoom
            // const currentCenter = mapRef.current.getCenter();
            // const offsetLat = (coordinates[1] - currentCenter.lat) * 0.03;
            // flyToOptions.center = [coordinates[0], coordinates[1] - offsetLat];

            // Start flying to location
            mapRef.current.flyTo(flyToOptions);

            // Mark project as directly clicked so downstream effects match marker-click behavior
            projectClickedDirectlyRef.current = true;

            // Sync selectedProject up to App (same as marker click)
            if (selectedProject !== sharedProject.id && onProjectChange) {
              onProjectChange(sharedProject.id);
            }

            // Wait for the fly animation to complete before showing project details
            mapRef.current.once("moveend", async () => {
              try {
                // Load landmarks for the shared project (parity with marker click)
                drowLandMark(sharedProject.id).catch((error) => {
                  console.error(
                    "❌ Error loading landmarks for shared project:",
                    error
                  );
                });

                // Add parcels source and layers for the shared project
                addParcelsSourceAndLayers(sharedProject.id);

                // Use pre-loaded project data if statistics are available, otherwise fetch
                if (
                  sharedProject.statistics &&
                  Object.keys(sharedProject.statistics).length > 0
                ) {
                  if (!isSharedUnitView) {
                    if (onProjectSelect) {
                      onProjectSelect(sharedProject);
                    } else {
                      setSelectedProjectData(sharedProject);
                      setIsProjectPanelVisible(true);
                    }
                  }
                } else {
                  // Fetch detailed project data with statistics
                  const response = await getProject(sharedProject.id);
                  const projectData = response.data.data;

                  if (!isSharedUnitView) {
                    if (onProjectSelect) {
                      onProjectSelect(projectData);
                    } else {
                      setSelectedProjectData(projectData);
                      setIsProjectPanelVisible(true);
                    }
                  }
                }
              } catch (error) {
                console.error("Error fetching shared project details:", error);
                // Fallback: use basic project data (only if not sharing a unit)
                if (!isSharedUnitView) {
                  if (onProjectSelect) {
                    onProjectSelect(sharedProject);
                  } else {
                    setSelectedProjectData(sharedProject);
                    setIsProjectPanelVisible(true);
                  }
                }
              }
            });
          } catch (error) {
            console.error("Error handling shared project:", error);
            // Fallback: use basic project data and fly to location
            if (!isSharedUnitView) {
              if (onProjectSelect) {
                onProjectSelect(sharedProject);
              } else {
                setSelectedProjectData(sharedProject);
                setIsProjectPanelVisible(true);
              }
            }
            // Use zoom level from database or default based on project type
            const zoomLevel = sharedProject.map_zoom_level !== null && sharedProject.map_zoom_level !== undefined
              ? sharedProject.map_zoom_level
              : (sharedProject.project_type === "residential" ? 18 : 14);
            mapRef.current.flyTo({ center: coordinates, zoom: zoomLevel });
          }
        };

        // Execute the simulation
        simulateProjectClick();
      } else {
        console.warn(
          "Shared project not found or missing coordinates:",
          sharedProjectId
        );
      }
    }
  }, [
    isSharedView,
    sharedProjectId,
    projects,
    dataLoaded,
    sharedProjectProcessed,
  ]);

  // Reset shared project processed flag when sharedProjectId changes
  useEffect(() => {
    if (sharedProjectId) {
      setSharedProjectProcessed(false);
    }
  }, [sharedProjectId]);

  // Shared-unit deep links (?shared=true&unit_id=...) are now resolved in App.js,
  // which fetches the unit and routes it through the same path used by the
  // search-results "select unit" action. Keeping that logic in one place lets
  // residential projects (whose parcels load per-floor) work the same as flat ones.

  // Handle page visibility changes (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {

      return;
      if (document.hidden) {
        // Page is hidden (user switched tabs)
        // Save current map state
        saveMapState();
      } else {
        // Page is visible again
        // Try to restore map state first
        const savedState = restoreMapState();

        // Validate map state and refresh if needed
        if (mapRef.current && typeof mapRef.current.getContainer === 'function') {
          try {
            // Trigger a resize to ensure map renders correctly
            setTimeout(() => {
              if (mapRef.current && typeof mapRef.current.resize === 'function') {
                mapRef.current.resize();
              }

              setTimeout(() => {
                validateAndRestoreMapData();
              }, 200);
            }, 100);
          } catch (error) {
            
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Add periodic validation of map data (every 2 minutes)
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     // Only validate if page is visible and map is ready
  //     if (!document.hidden && mapRef.current && mapRef.current.isStyleLoaded()) {
  //       validateAndRestoreMapData();
  //     }
  //   }, 2 * 60 * 1000); // 2 minutes

  //   return () => clearInterval(interval);
  // }, [projects, selectedProject]);

  // Update map styling when selection changes
  useEffect(() => {
    updateMapStyling();
  }, [selectedBlockId, selectedParcelId]);

  // Track user style selection to prevent automatic switching
  useEffect(() => {
    // If mapStyle changes and it's not part of automatic switching, mark as user selected
    if (mapStyle !== "satellite-v9" && mapStyle !== "satellite-streets-v12") {
      setUserSelectedStyle(mapStyle);
    } else {
      // Reset user selection when switching back to satellite styles
      if (
        userSelectedStyle &&
        (mapStyle === "satellite-v9" || mapStyle === "satellite-streets-v12")
      ) {
        setUserSelectedStyle(null);
      }
    }
  }, [mapStyle, userSelectedStyle]);

  // Update map style when mapStyle prop changes
  useEffect(() => {
    console.log(
      "OptimizedMap: mapStyle useEffect triggered with:",
      mapStyle,
      "Map ready:",
      !!mapRef.current,
      "Style loaded:",
      mapRef.current?.isStyleLoaded()
    );

    const changeStyle = () => {
      if (mapRef.current && mapRef.current.isStyleLoaded()) {

        // Store current data before style change
        const currentProjects = [...projects];
        const currentMapLayers = [...mapLayers];
        const currentSelectedBlockId = selectedBlockId;
        const currentSelectedParcelId = selectedParcelId;
        const currentSelectedProject = selectedProject;
        const currentFilters = { ...activeFilters };

        // Store current data from sources
        let currentBlocksData = null;
        let currentParcelsData = null;

        const blocksSource = mapRef.current.getSource("blocks");
        const parcelsSource = mapRef.current.getSource("parcels");

        if (blocksSource && blocksSource._data) {
          currentBlocksData = JSON.parse(JSON.stringify(blocksSource._data));
        }

        if (parcelsSource && parcelsSource._data) {
          currentParcelsData = JSON.parse(JSON.stringify(parcelsSource._data));
        }

        //   projects: currentProjects.length,
        //   mapLayers: currentMapLayers.length,
        //   blocks: currentBlocksData?.features?.length || 0,
        //   parcels: currentParcelsData?.features?.length || 0,
        //   selectedProject: currentSelectedProject,
        // });

        // Clean up existing popups
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }

        // Hide parcel dimensions before style change
        hideParcelDimensions();

        // Style change happens silently in the background

        // Determine the style URL based on the mapStyle
        let styleUrl;
        if (mapStyle === "satellite-v9") {
          styleUrl = `${window.location.origin}/styles/satellite-custom.json`;
        } else {
          styleUrl = `mapbox://styles/mapbox/${mapStyle}`;
        }

        mapRef.current.setStyle(styleUrl);

        // Re-add sources and layers after style change
        mapRef.current.once("styledata", () => {

          // Wait for style to be fully loaded
          const restoreEverything = () => {
            try {
              if (mapRef.current && mapRef.current.isStyleLoaded()) {

                // Re-create all base sources and layers first
                recreateBaseLayers();

                // Restore data with proper timing
                setTimeout(async () => {

                  try {
                    // Restore project markers first
                    if (currentProjects.length > 0) {
                      updateProjectMarkers(currentProjects);
                    }

                    // Restore blocks and parcels data
                    if (
                      currentBlocksData &&
                      currentBlocksData.features &&
                      currentBlocksData.features.length > 0
                    ) {
                      const blocksSource = mapRef.current.getSource("blocks");
                      if (blocksSource) {
                        blocksSource.setData(currentBlocksData);
                        //   "Restored blocks:",
                        //   currentBlocksData.features.length
                        // );
                      }
                    }

                    if (
                      currentParcelsData &&
                      currentParcelsData.features &&
                      currentParcelsData.features.length > 0
                    ) {
                      const parcelsSource = mapRef.current.getSource("parcels");
                      if (parcelsSource) {
                        parcelsSource.setData(currentParcelsData);
                        //   "Restored parcels:",
                        //   currentParcelsData.features.length
                        // );
                      }
                    }

                    // Restore map layers (images and GeoJSON)
                    if (currentMapLayers.length > 0) {
                      await addMapLayersToMap(currentMapLayers);
                    }

                    // Restore landmarks if there was a selected project
                    if (currentSelectedProject) {
                      //   "Restoring landmarks for project:",
                      //   currentSelectedProject
                      // );
                      await drowLandMark(currentSelectedProject);
                    }

                    // Wait a bit more before restoring selections and filters
                    setTimeout(() => {
                      // Restore selections
                      if (currentSelectedBlockId) {
                        setSelectedBlockId(currentSelectedBlockId);
                      }
                      if (currentSelectedParcelId) {
                        setSelectedParcelId(currentSelectedParcelId);
                      }

                      // Update styling
                      updateMapStyling();

                      // Restore filters
                      if (
                        currentFilters &&
                        (currentFilters.salesStatus?.length > 0 ||
                          currentFilters.services?.length > 0 ||
                          currentFilters.priceRange?.min > 0 ||
                          currentFilters.priceRange?.max < 1000000)
                      ) {
                        applyFiltersToMap(currentFilters);
                      }

                    }, 300);
                  } catch (restoreError) {
                    console.error(
                      "Error during data restoration:",
                      restoreError
                    );
                  }
                }, 500);
              } else {
                setTimeout(restoreEverything, 200);
              }
            } catch (error) {
              console.error("Error during style restoration:", error);
            }
          };

          restoreEverything();
        });

        // Handle style change errors
        mapRef.current.once("styleerror", (error) => {
          console.error("Map style error:", error);
        });

        // Fallback timeout in case styledata event doesn't fire
        setTimeout(() => {
        }, 15000);
      } else {
        // Try again after a short delay if map is not ready
        const retryTimer = setTimeout(() => {
          if (mapRef.current) {
            changeStyle();
          }
        }, 1000);

        return () => clearTimeout(retryTimer);
      }
    };

    changeStyle();
  }, [mapStyle]);

  // Show loading indicator while map configuration is loading
  if (configLoading) {
    return (
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        {/* <div style={{ textAlign: "center" }}>
          <img
            src={APP_PATHS.PUBLIC_ASSETS.LOGO_ONLY}
            alt="Loading..."
            style={{
              width: "64px",
              height: "64px",
              animation: "pulse 2s infinite"
            }}
          />
          <p style={{
            marginTop: "16px",
            color: "#666",
            fontSize: "14px"
          }}>
            {language === 'ar' ? 'جاري تحميل إعدادات الخريطة...' : 'Loading map settings...'}
          </p>
        </div> */}
      </div>
    );
  }

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      <div ref={mapContainerRef} style={{ height: "100%", width: "100%" }} />

      <MapControls
        mapStyle={mapStyle}
       onMapStyleChange={onMapStyleChange}
        onLanguageToggle={onLanguageToggle}
        onSidebarPositionToggle={onSidebarPositionToggle}
        language={language}
        sidebarPosition={sidebarPosition}
        actualSidebarPosition={actualSidebarPosition}
        mapRef={mapRef}
        isUnitDetailsVisible={selectedProject != null}
        isProjectDetailsVisible={isProjectPanelVisible}
        onSubHomeClick={handleSubHomeClick}
        onHomeClick={handleHomeClick}
        isSharedView={isSharedView}
        currentZoom={currentZoom}
      />

      {/* LoadingOverlay removed - using background loading instead */}

      {/* <LayersLoadingIndicator
        isVisible={isLayersLoading}
        message={layersLoadingMessage}
      /> */}

      <MapFilters
        isVisible={filtersVisible}
        onClose={onToggleFilters}
        onFiltersChange={handleFiltersChange}
        language={language}
        parcelsData={allParcelsData}
      />

      <RealMapLogo />

      <RightControls
        mapRef={mapRef}
        language={language}
        onShowFavorites={onShowFavorites}
      />

      <TermsNotification />

      {/* <ProjectDetailsPanel
        // projectData={selectedProjectData}
        // language={language}
        // isVisible={isProjectPanelVisible}
        isVisible={false}

        // onClose={handleCloseProjectPanel}
      /> */}


    </div>
  );
};

export default OptimizedMap;

